/**
 * BulkGenerationStep
 *
 * Step 5 of the Bulk Content Planner.
 * Generates creatives (images/videos/carousels) from approved briefs in bulk.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Play,
    Pause,
    CheckCircle,
    XCircle,
    Loader2,
    Image,
    Video,
    Layers,
    AlertCircle,
    RefreshCw,
    Clock,
    CloudOff,
    Cloud,
    Wand2,
    X,
    Info,
    ChevronLeft,
    ChevronRight,
    Crop,
    Paintbrush,
    Trash2,
    Search,
    Maximize2,
    Check,
    CheckSquare,
    Square,
    Package,
    ChevronDown,
    BookmarkPlus,
    Undo2,
    Pencil,
    ArrowRight
} from 'lucide-react';
import { processBulkGeneration, retryFailedGenerations, generateCreativeFromBrief } from '../../services/bulkGenerationService';
import { useBulkGenerationJob, startBackgroundGeneration, cancelJob } from '../../hooks/useBulkGenerationJob';
import { useApp } from '../../contexts/AppContext';
import { updateLibraryItem, uploadLibraryImage, addRefinedVersion, addCarouselCardRefinedVersion, setCarouselCardActiveVersion, loadLibraryItems, getLibraryItem, setActiveVersion, deleteLibraryItem } from '../../services/libraryService';
import { updateContentBrief } from '../../services/contentPlanService';
import ImageRefinementPanel from '../ImageRefinementPanel';
import ImageCropCanvas from '../ImageCropCanvas';
import MaskDrawingCanvas from '../MaskDrawingCanvas';

const OUTPUT_TYPE_ICONS = {
    image: Image,
    video: Video,
    carousel: Layers
};

const OUTPUT_TYPE_LABELS = {
    image: 'Image',
    video: 'Video',
    carousel: 'Carousel'
};

const BulkGenerationStep = ({ briefs, setBriefs, brand, planId, onComplete, onBack, initialJobId = null, products = [], targetPlatforms = [], onGenerationStateChange }) => {
    const { user: currentUser, selectedBrand } = useApp();
    const [status, setStatus] = useState('idle'); // idle, generating, paused, complete, error, background
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [results, setResults] = useState([]);
    const [currentBrief, setCurrentBrief] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    const [error, setError] = useState(null);
    const [retryInfo, setRetryInfo] = useState(null); // { type, attempt, maxAttempts, retryInSeconds, message }
    const [waitMessage, setWaitMessage] = useState(null);
    const [selectedResult, setSelectedResult] = useState(null); // For detail/refinement modal
    const [selectedLibraryItem, setSelectedLibraryItem] = useState(null); // Library item with history for selected result
    const [isLoadingLibraryItem, setIsLoadingLibraryItem] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0); // For carousel image navigation
    const [isRefining, setIsRefining] = useState(false);
    const [refinementError, setRefinementError] = useState(null);
    const [refinementSuccess, setRefinementSuccess] = useState(false);

    // Regenerate state
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regeneratePrompt, setRegeneratePrompt] = useState('');
    const [regenerateError, setRegenerateError] = useState(null);
    const [regenerateSuccess, setRegenerateSuccess] = useState(false);

    // Inpainting state
    const [showMaskDrawer, setShowMaskDrawer] = useState(false);
    const [isInpainting, setIsInpainting] = useState(false);
    const [inpaintMode, setInpaintMode] = useState('remove'); // 'remove' or 'replace'
    const [inpaintError, setInpaintError] = useState(null);

    // Crop state
    const [showCropCanvas, setShowCropCanvas] = useState(false);

    // Background removal state
    const [isRemovingBackground, setIsRemovingBackground] = useState(false);
    const [bgRemovalError, setBgRemovalError] = useState(null);

    // Fullscreen preview state
    const [showFullscreen, setShowFullscreen] = useState(false);

    // Track if we've already synced results for a completed job (to prevent overwriting local refinement updates)
    // Using a ref so async functions always see the current value
    const syncedJobIdRef = useRef(null);

    // Library picker state
    const [showLibraryPicker, setShowLibraryPicker] = useState(false);
    const [libraryImages, setLibraryImages] = useState([]);
    const [libraryLoading, setLibraryLoading] = useState(false);
    const [librarySearch, setLibrarySearch] = useState('');
    const [librarySelected, setLibrarySelected] = useState([]);
    const [addLibraryImagesCallback, setAddLibraryImagesCallback] = useState(null);

    // Bulk selection state
    const [bulkSelectionMode, setBulkSelectionMode] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    // Structure: Array<{ briefId, cardIndex?, imageUrl, libraryItemId }>
    const [expandedCarouselBriefId, setExpandedCarouselBriefId] = useState(null);

    // Bulk refinement state
    const [showBulkRefinementModal, setShowBulkRefinementModal] = useState(false);
    const [bulkRefinementProgress, setBulkRefinementProgress] = useState({
        current: 0, total: 0, currentImage: null, status: 'idle', results: []
    });
    const [isBulkRefining, setIsBulkRefining] = useState(false);
    const [bulkRefinementError, setBulkRefinementError] = useState(null);
    const [bulkRefinementPrompt, setBulkRefinementPrompt] = useState(''); // Store prompt for retry

    // Product change state
    const [showProductSelector, setShowProductSelector] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [isRegeneratingWithProduct, setIsRegeneratingWithProduct] = useState(false);
    const [productRegenerateError, setProductRegenerateError] = useState(null);

    // Approval workflow state
    const [approvalStatuses, setApprovalStatuses] = useState({});
    // e.g. { briefId1: 'approved', briefId2: 'saved', briefId3: 'discarded' }
    const [isFinalizingApprovals, setIsFinalizingApprovals] = useState(false);

    // Caption editing state
    const [isEditingCaption, setIsEditingCaption] = useState(false);
    const [editedCaption, setEditedCaption] = useState('');
    const [isSavingCaption, setIsSavingCaption] = useState(false);

    // Background job state
    const [backgroundJobId, setBackgroundJobId] = useState(initialJobId);
    const { job: backgroundJob, loading: jobLoading } = useBulkGenerationJob(backgroundJobId);

    // Check for existing job for this plan on mount (active or completed)
    useEffect(() => {
        const checkExistingJob = async () => {
            if (backgroundJobId || !planId || !currentUser?.uid) return;

            try {
                const { collection, query, where, getDocs, orderBy, limit } = await import('firebase/firestore');
                const { db } = await import('../../lib/firebase');

                // First, look for active jobs for this plan
                const activeJobsQuery = query(
                    collection(db, 'bulkGenerationJobs'),
                    where('userId', '==', currentUser.uid),
                    where('planId', '==', planId),
                    where('status', 'in', ['pending', 'processing']),
                    orderBy('createdAt', 'desc'),
                    limit(1)
                );

                let snapshot = await getDocs(activeJobsQuery);
                if (!snapshot.empty) {
                    const existingJob = snapshot.docs[0];
                    console.log('Found existing active job:', existingJob.id);
                    setBackgroundJobId(existingJob.id);
                    setStatus('background');
                    return;
                }

                // If no active job, look for recently completed job for this plan
                // This enables resuming after background generation completes
                const completedJobsQuery = query(
                    collection(db, 'bulkGenerationJobs'),
                    where('userId', '==', currentUser.uid),
                    where('planId', '==', planId),
                    where('status', '==', 'completed'),
                    orderBy('createdAt', 'desc'),
                    limit(1)
                );

                snapshot = await getDocs(completedJobsQuery);
                if (!snapshot.empty) {
                    const completedJob = snapshot.docs[0];
                    console.log('Found completed job for resume:', completedJob.id);
                    setBackgroundJobId(completedJob.id);
                    // Status will be set to 'complete' by the useBulkGenerationJob sync effect
                }
            } catch (err) {
                console.log('Could not check for existing jobs:', err.message);
            }
        };

        checkExistingJob();
    }, [planId, currentUser?.uid, backgroundJobId]);

    // Filter to only approved briefs
    const approvedBriefs = briefs.filter(b => b.status === 'approved');

    // Creative type count state (for redistribution)
    const [typeCounts, setTypeCounts] = useState(() => {
        const counts = { image: 0, video: 0, carousel: 0 };
        approvedBriefs.forEach(b => {
            if (counts[b.outputType] !== undefined) {
                counts[b.outputType]++;
            }
        });
        return counts;
    });

    // Keyboard shortcuts for fullscreen preview
    useEffect(() => {
        if (!showFullscreen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setShowFullscreen(false);
            } else if (e.key === 'ArrowLeft' && selectedResult?.carouselImages?.length > 1) {
                setCarouselIndex(prev => prev > 0 ? prev - 1 : selectedResult.carouselImages.length - 1);
            } else if (e.key === 'ArrowRight' && selectedResult?.carouselImages?.length > 1) {
                setCarouselIndex(prev => prev < selectedResult.carouselImages.length - 1 ? prev + 1 : 0);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showFullscreen, selectedResult]);

    // Sync state from background job updates
    useEffect(() => {
        if (!backgroundJob) return;

        // Update progress from background job
        if (backgroundJob.progress) {
            setProgress({
                current: backgroundJob.progress.current || 0,
                total: backgroundJob.progress.total || approvedBriefs.length
            });
        }

        // Update results from background job briefs
        // For completed jobs, fetch library items to get latest images (including regenerations)
        const syncResults = async () => {
            if (!backgroundJob.briefs) return;

            const completedBriefs = backgroundJob.briefs.filter(b => b.status === 'completed' || b.status === 'failed');

            // If job is completed, fetch library items to get latest regenerated images
            // BUT only if we haven't already synced for this job (to preserve local refinement updates)
            if (backgroundJob.status === 'completed') {
                // Skip re-fetching if we've already synced this completed job
                // Local refinements update both results state and library items,
                // so we don't want to overwrite local state with a re-fetch
                if (syncedJobIdRef.current === backgroundJob.id) {
                    return;
                }

                const jobResults = await Promise.all(completedBriefs.map(async (b) => {
                    const baseResult = {
                        briefId: b.id,
                        success: b.status === 'completed',
                        error: b.error,
                        imageUrl: b.result?.imageUrl,
                        thumbnailUrl: b.result?.thumbnailUrl,
                        libraryItemId: b.result?.libraryItemId,
                        outputType: b.result?.outputType || 'image',
                        carouselImages: b.result?.carouselImages || null
                    };

                    // Fetch library item to get latest images (refinements/regenerations update library items, not job data)
                    if (b.result?.libraryItemId && b.status === 'completed') {
                        try {
                            const libraryItem = await getLibraryItem(b.result.libraryItemId);
                            if (libraryItem) {
                                // Check both root carouselImages (refinements) and metadata.carouselImages (legacy regenerations)
                                let latestCarouselImages = libraryItem.carouselImages || libraryItem.metadata?.carouselImages || baseResult.carouselImages;

                                // If carouselImages exists in library item, merge with base result to preserve all card data
                                // Refinements only update individual cards, so we need the original data for unchanged cards
                                if (latestCarouselImages && baseResult.carouselImages) {
                                    // Normalize to array if it's an object with numeric keys (Firestore dot notation artifact)
                                    if (!Array.isArray(latestCarouselImages) && typeof latestCarouselImages === 'object') {
                                        const maxIndex = Math.max(...Object.keys(latestCarouselImages).map(k => parseInt(k, 10)).filter(n => !isNaN(n)), -1);
                                        latestCarouselImages = Array.from({ length: maxIndex + 1 }, (_, i) => latestCarouselImages[i] || null);
                                    }

                                    // Merge: use library item data where available, fall back to base result
                                    latestCarouselImages = baseResult.carouselImages.map((baseCard, idx) => {
                                        const libraryCard = latestCarouselImages[idx];
                                        if (libraryCard && (libraryCard.imageUrl || libraryCard.url)) {
                                            return {
                                                ...baseCard,
                                                ...libraryCard,
                                                imageUrl: libraryCard.imageUrl || libraryCard.url || baseCard.imageUrl,
                                                url: libraryCard.url || libraryCard.imageUrl || baseCard.url
                                            };
                                        }
                                        return baseCard;
                                    });
                                }

                                return {
                                    ...baseResult,
                                    imageUrl: libraryItem.imageUrl || baseResult.imageUrl,
                                    thumbnailUrl: libraryItem.thumbnailUrl || libraryItem.imageUrl || baseResult.thumbnailUrl,
                                    carouselImages: latestCarouselImages
                                };
                            }
                        } catch (err) {
                            console.warn('Failed to fetch library item:', err);
                        }
                    }
                    return baseResult;
                }));
                setResults(jobResults);
                // Mark this job as synced so we don't overwrite local refinement updates
                syncedJobIdRef.current = backgroundJob.id;
            } else {
                // For in-progress jobs, use job data directly (no regenerations possible yet)
                const jobResults = completedBriefs.map(b => ({
                    briefId: b.id,
                    success: b.status === 'completed',
                    error: b.error,
                    imageUrl: b.result?.imageUrl,
                    thumbnailUrl: b.result?.thumbnailUrl,
                    libraryItemId: b.result?.libraryItemId,
                    outputType: b.result?.outputType || 'image',
                    carouselImages: b.result?.carouselImages || null
                }));
                setResults(jobResults);
            }
        };

        syncResults();

        // Update status based on job status
        if (backgroundJob.status === 'completed') {
            setStatus('complete');
        } else if (backgroundJob.status === 'failed') {
            setStatus('error');
            setError(backgroundJob.error || 'Background generation failed');
        } else if (backgroundJob.status === 'cancelled') {
            setStatus('idle');
            setBackgroundJobId(null);
        } else if (backgroundJob.status === 'processing') {
            setStatus('background');
        }
    }, [backgroundJob, approvedBriefs.length]);

    // Initialize approval statuses for new successful results
    useEffect(() => {
        setApprovalStatuses(prev => {
            const next = { ...prev };
            let changed = false;
            results.filter(r => r.success).forEach(r => {
                if (!next[r.briefId]) {
                    next[r.briefId] = 'pending';
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [results]);

    // Calculate stats
    const successCount = results.filter(r => r.success).length;
    const failedCount = results.filter(r => !r.success).length;
    const failedBriefs = results
        .filter(r => !r.success)
        .map(r => briefs.find(b => b.id === r.briefId))
        .filter(Boolean);

    // Approval counts
    const approvedCount = results.filter(r => r.success && approvalStatuses[r.briefId] === 'approved').length;
    const savedCount = results.filter(r => r.success && approvalStatuses[r.briefId] === 'saved').length;
    const discardedCount = results.filter(r => r.success && approvalStatuses[r.briefId] === 'discarded').length;
    const pendingApprovalCount = results.filter(r => r.success && (!approvalStatuses[r.briefId] || approvalStatuses[r.briefId] === 'pending')).length;

    // Handle progress updates
    const handleProgress = useCallback((progressData) => {
        setProgress({
            current: progressData.current,
            total: progressData.total
        });

        if (progressData.brief) {
            setCurrentBrief(progressData.brief);
        }

        // Handle retry info
        if (progressData.phase === 'retrying' && progressData.retry) {
            setRetryInfo(progressData.retry);
            setWaitMessage(null);
        } else if (progressData.phase === 'waiting') {
            setWaitMessage(progressData.message);
            setRetryInfo(null);
        } else if (progressData.phase === 'generating') {
            setRetryInfo(null);
            setWaitMessage(null);
        }

        if (progressData.status === 'success' || progressData.status === 'failed') {
            setRetryInfo(null);
            setWaitMessage(null);
            setResults(prev => {
                const existing = prev.find(r => r.briefId === progressData.brief?.id);
                if (existing) return prev;

                return [...prev, {
                    briefId: progressData.brief?.id,
                    success: progressData.status === 'success',
                    error: progressData.error,
                    imageUrl: progressData.imageUrl,
                    thumbnailUrl: progressData.thumbnailUrl,
                    libraryItemId: progressData.libraryItemId,
                    outputType: progressData.outputType || 'image',
                    carouselImages: progressData.carouselImages || null
                }];
            });
        }
    }, []);

    // Notify parent when generation state changes
    // Only block modal closing for client-side generation, not background jobs
    useEffect(() => {
        const isClientSideActive = status === 'generating'; // Background jobs can continue if modal closes
        onGenerationStateChange?.(isClientSideActive);
    }, [status, onGenerationStateChange]);

    // Start generation
    const startGeneration = async () => {
        if (approvedBriefs.length === 0) {
            setError('No approved briefs to generate. Go back and approve some briefs first.');
            return;
        }

        setStatus('generating');
        setError(null);
        setResults([]);
        syncedJobIdRef.current = null; // Reset so new generation can sync
        setProgress({ current: 0, total: approvedBriefs.length });

        try {
            const generationResults = await processBulkGeneration(
                approvedBriefs,
                brand,
                handleProgress,
                { products, targetPlatforms } // Pass products and platforms for reference images and aspect ratio
            );

            setResults(generationResults);
            setStatus('complete');
        } catch (err) {
            console.error('Bulk generation error:', err);
            setError(err.message);
            setStatus('error');
        }
    };

    // Retry failed briefs using background processing
    const retryFailed = async () => {
        if (failedBriefs.length === 0) return;

        setStatus('background');
        setError(null);

        // Remove failed results before retry
        setResults(prev => prev.filter(r => r.success));
        setProgress({ current: successCount, total: approvedBriefs.length });

        try {
            const userId = currentUser?.uid;
            const brandId = selectedBrand?.id || brand?.id;

            if (!userId || !brandId) {
                throw new Error('User or brand not found');
            }

            const { jobId } = await startBackgroundGeneration(
                failedBriefs,
                brand,
                userId,
                brandId,
                planId,
                products
            );

            setBackgroundJobId(jobId);
            console.log('Started background retry job:', jobId);
        } catch (err) {
            console.error('Retry failed:', err);
            setError(err.message);
            setStatus('error');
        }
    };

    // Start background generation (server-side processing)
    const startBackgroundJob = async () => {
        if (approvedBriefs.length === 0) {
            setError('No approved briefs to generate. Go back and approve some briefs first.');
            return;
        }

        setStatus('background');
        setError(null);
        setResults([]);
        syncedJobIdRef.current = null; // Reset so new job will sync
        setProgress({ current: 0, total: approvedBriefs.length });

        try {
            const userId = currentUser?.uid;
            const brandId = selectedBrand?.id || brand?.id;

            if (!userId || !brandId) {
                throw new Error('User or brand not found');
            }

            const { jobId } = await startBackgroundGeneration(
                approvedBriefs,
                brand,
                userId,
                brandId,
                planId,
                products
            );

            setBackgroundJobId(jobId);
            console.log('Started background generation job:', jobId);

        } catch (err) {
            console.error('Failed to start background generation:', err);
            setError(err.message);
            setStatus('error');
        }
    };

    // Cancel background job
    const handleCancelBackground = async () => {
        if (!backgroundJobId) return;

        try {
            await cancelJob(backgroundJobId);
            setBackgroundJobId(null);
            setStatus('idle');
        } catch (err) {
            console.error('Failed to cancel job:', err);
        }
    };

    // Approval action handlers
    // Save edited caption to brief (local state + Firestore)
    const handleSaveCaption = async (briefId, newCaption) => {
        if (!briefId) return;
        setIsSavingCaption(true);
        try {
            // Update in Firestore
            await updateContentBrief(briefId, { 'brief.caption': newCaption });
            // Update local briefs state
            setBriefs(prev => prev.map(b => {
                if (b.id === briefId) {
                    return {
                        ...b,
                        brief: { ...(b.brief || {}), caption: newCaption }
                    };
                }
                return b;
            }));
            setIsEditingCaption(false);
        } catch (err) {
            console.error('Failed to save caption:', err);
        } finally {
            setIsSavingCaption(false);
        }
    };

    const handleApprove = (briefId) => setApprovalStatuses(prev => ({
        ...prev, [briefId]: prev[briefId] === 'approved' ? 'pending' : 'approved'
    }));
    const handleSaveToLibrary = (briefId) => setApprovalStatuses(prev => ({
        ...prev, [briefId]: prev[briefId] === 'saved' ? 'pending' : 'saved'
    }));
    const handleDiscardResult = (briefId) => setApprovalStatuses(prev => ({
        ...prev, [briefId]: prev[briefId] === 'discarded' ? 'pending' : 'discarded'
    }));
    const handleApproveAll = () => {
        setApprovalStatuses(prev => {
            const next = { ...prev };
            results.filter(r => r.success).forEach(r => { next[r.briefId] = 'approved'; });
            return next;
        });
    };

    // Handle completion — only pass approved results, delete discarded from library
    const handleComplete = async () => {
        console.log('[BulkGenerationStep] handleComplete called, results:', results);
        if (!onComplete) {
            console.error('[BulkGenerationStep] onComplete callback is not defined!');
            return;
        }

        setIsFinalizingApprovals(true);
        try {
            // Delete discarded items from library
            const discardedResults = results.filter(r => approvalStatuses[r.briefId] === 'discarded' && r.libraryItemId);
            for (const result of discardedResults) {
                try {
                    await deleteLibraryItem(result.libraryItemId, currentUser.uid);
                } catch (err) {
                    console.error('Failed to delete discarded library item:', result.libraryItemId, err);
                }
            }

            // Only pass approved results to scheduling
            const approvedResults = results.filter(r => approvalStatuses[r.briefId] === 'approved');
            onComplete(approvedResults);
        } finally {
            setIsFinalizingApprovals(false);
        }
    };

    // Handle creative refinement (same logic as AssetLibrary)
    const handleRefineCreative = async (prompt, userReferenceImages = [], regenerateFromScratch = false) => {
        if (!selectedResult || !prompt?.trim() || !currentUser?.uid || !selectedBrand?.id) {
            return;
        }

        const imageToRefine = getCurrentImage();
        if (!imageToRefine && !regenerateFromScratch) {
            setRefinementError('No image available to refine');
            return;
        }

        const carouselImages = selectedResult.carouselImages || [];
        const isCarousel = carouselImages.length > 1;

        // Filter to only valid reference images
        const validRefImages = userReferenceImages.filter(img => img.status === 'valid' && img.url);

        setIsRefining(true);
        setRefinementError(null);
        setRefinementSuccess(false);

        try {
            // Build reference images array for API
            const referenceImages = [];

            // Only include original image if not regenerating from scratch
            if (!regenerateFromScratch && imageToRefine) {
                referenceImages.push({
                    url: imageToRefine,
                    instruction: `[ORIGINAL IMAGE] - Edit this image according to the instructions below.`
                });
            }

            // Add original product photos so the AI maintains product fidelity
            const brief = briefs.find(b => b.id === selectedResult.briefId);
            const briefData = brief?.brief || brief || {};
            const product = products?.find(p =>
                p.id === brief?.productId || p.name === briefData.product
            ) || null;

            if (product?.images?.length > 0) {
                product.images.slice(0, 3).forEach((img, idx) => {
                    const imgUrl = img.url || img;
                    if (imgUrl) {
                        referenceImages.push({
                            url: imgUrl,
                            instruction: `[PRODUCT PHOTO ${idx + 1}] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
                        });
                    }
                });
            } else if (product?.imageUrl) {
                referenceImages.push({
                    url: product.imageUrl,
                    instruction: `[PRODUCT PHOTO] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
                });
            }

            // Add brand logo
            const logoUrl = brand?.logoUrl || brand?.logo || brand?.branding?.logoUrl;
            if (logoUrl) {
                referenceImages.push({
                    url: logoUrl,
                    instruction: `[BRAND LOGO] - HIGH-FIDELITY OBJECT. Reproduce this logo EXACTLY as-is. Do not recolor, distort, or modify it in any way.`
                });
            }

            // Add user's labeled reference images (always included)
            validRefImages.forEach((refImg) => {
                referenceImages.push({
                    url: refImg.url,
                    instruction: `[${refImg.label}] - Use elements from this reference image as directed.`
                });
            });

            // Build the prompt based on whether we're regenerating or refining
            let refinementPrompt;
            if (regenerateFromScratch) {
                // For regeneration, just use the prompt directly with any reference images
                const refImageMentions = validRefImages.length > 0
                    ? `\nReference images provided: ${validRefImages.map(img => img.label).join(', ')}.`
                    : '';
                refinementPrompt = `${prompt.trim()}${refImageMentions}`;
            } else {
                // For refinement, include instructions to edit the original
                const refImageMentions = validRefImages.length > 0
                    ? `\nReference images provided: ${validRefImages.map(img => img.label).join(', ')}.`
                    : '';
                refinementPrompt = `Edit the [ORIGINAL IMAGE].${refImageMentions}

${prompt.trim()}

Keep everything else about the original image unchanged.`;
            }

            // Call the image generation API
            const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: refinementPrompt,
                    aspectRatio: '1:1',
                    referenceImages: referenceImages,
                    userId: currentUser.uid
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorData = {};
                try { errorData = JSON.parse(errorText); } catch (e) { }
                throw new Error(errorData.details || errorData.error || 'Failed to generate refined image');
            }

            const data = await response.json();
            if (!data.url) {
                throw new Error('No image URL in response');
            }

            // Upload the refined image to storage if it's base64
            let finalImageUrl = data.url;
            if (finalImageUrl.startsWith('data:image')) {
                finalImageUrl = await uploadLibraryImage(
                    finalImageUrl,
                    currentUser.uid,
                    selectedBrand.id
                );
            }

            // Update the library item with the new image
            if (selectedResult.libraryItemId) {
                if (isCarousel) {
                    // For carousels, track history per card
                    const updatedItem = await addCarouselCardRefinedVersion(
                        selectedResult.libraryItemId,
                        carouselIndex,
                        finalImageUrl,
                        prompt.trim(),
                        currentUser.uid,
                        carouselImages // Pass existing carousel images from result
                    );
                    if (updatedItem) {
                        setSelectedLibraryItem(updatedItem);
                    }
                } else {
                    // For single images, use addRefinedVersion to track history
                    const updatedItem = await addRefinedVersion(
                        selectedResult.libraryItemId,
                        finalImageUrl,
                        prompt.trim(),
                        currentUser.uid
                    );
                    // Update selected library item with new history
                    if (updatedItem) {
                        setSelectedLibraryItem(updatedItem);
                    }
                }
            }

            // Update local results state
            setResults(prev => prev.map(r => {
                if (r.briefId === selectedResult.briefId) {
                    if (isCarousel) {
                        const updatedCarousel = [...(r.carouselImages || [])];
                        if (updatedCarousel[carouselIndex]) {
                            updatedCarousel[carouselIndex] = {
                                ...updatedCarousel[carouselIndex],
                                imageUrl: finalImageUrl,
                                url: finalImageUrl
                            };
                        }
                        return { ...r, carouselImages: updatedCarousel };
                    }
                    return {
                        ...r,
                        imageUrl: finalImageUrl,
                        thumbnailUrl: finalImageUrl
                    };
                }
                return r;
            }));

            // Update selectedResult to show new image
            setSelectedResult(prev => {
                if (isCarousel) {
                    const updatedCarousel = [...(prev.carouselImages || [])];
                    if (updatedCarousel[carouselIndex]) {
                        updatedCarousel[carouselIndex] = {
                            ...updatedCarousel[carouselIndex],
                            imageUrl: finalImageUrl,
                            url: finalImageUrl
                        };
                    }
                    return { ...prev, carouselImages: updatedCarousel };
                }
                return {
                    ...prev,
                    imageUrl: finalImageUrl,
                    thumbnailUrl: finalImageUrl
                };
            });

            // Also update briefs so the new image carries forward
            if (setBriefs) {
                setBriefs(prev => prev.map(b => {
                    if (b.id === selectedResult.briefId) {
                        if (isCarousel) {
                            const updatedCarousel = [...(b.carouselImages || [])];
                            if (updatedCarousel[carouselIndex]) {
                                updatedCarousel[carouselIndex] = {
                                    ...updatedCarousel[carouselIndex],
                                    imageUrl: finalImageUrl,
                                    url: finalImageUrl
                                };
                            }
                            return { ...b, carouselImages: updatedCarousel };
                        }
                        return {
                            ...b,
                            generatedImageUrl: finalImageUrl,
                            generatedThumbnailUrl: finalImageUrl
                        };
                    }
                    return b;
                }));
            }

            setRefinementSuccess(true);
            setTimeout(() => setRefinementSuccess(false), 3000);

        } catch (err) {
            console.error('Refinement failed:', err);
            setRefinementError(err.message || 'Failed to refine image');
        } finally {
            setIsRefining(false);
        }
    };

    // Handle full regeneration of a creative (all carousel cards, or single image/video)
    const handleRegenerate = async () => {
        if (!selectedResult || !regeneratePrompt?.trim() || !currentUser?.uid) return;

        const brief = briefs.find(b => b.id === selectedResult.briefId);
        if (!brief) {
            setRegenerateError('Brief not found');
            return;
        }

        setIsRegenerating(true);
        setRegenerateError(null);
        setRegenerateSuccess(false);

        try {
            const briefData = brief.brief || brief;
            const outputType = brief.outputType || briefData.outputType || 'image';
            const refinementInstruction = regeneratePrompt.trim();

            // Build a modified brief with the refinement prompt injected into scene descriptions
            const modifiedBriefData = { ...briefData };

            if (outputType === 'carousel' && modifiedBriefData.carouselCards?.length > 0) {
                // Inject refinement prompt into each carousel card's scene description
                modifiedBriefData.carouselCards = modifiedBriefData.carouselCards.map(card => ({
                    ...card,
                    sceneDescription: card.sceneDescription
                        ? `${card.sceneDescription}\n\nADDITIONAL DIRECTION: ${refinementInstruction}`
                        : refinementInstruction
                }));
            }

            // Inject into the main scene description too
            if (modifiedBriefData.sceneDescription) {
                modifiedBriefData.sceneDescription = `${modifiedBriefData.sceneDescription}\n\nADDITIONAL DIRECTION: ${refinementInstruction}`;
            } else {
                modifiedBriefData.sceneDescription = refinementInstruction;
            }

            const modifiedBrief = {
                ...brief,
                brief: modifiedBriefData,
                outputType
            };

            // Find the matching product for reference images
            const product = products?.find(p =>
                p.name === briefData.product || p.id === brief.productId
            ) || null;

            // Build reference images from brand and product
            const referenceImages = [];
            if (brand?.visual?.logoUrl) {
                referenceImages.push({
                    url: brand.visual.logoUrl,
                    instruction: 'BRAND LOGO - Include this logo in the image where specified.'
                });
            }
            if (product?.images?.length > 0) {
                product.images.slice(0, 2).forEach(img => {
                    referenceImages.push({
                        url: img.url || img,
                        instruction: 'PRODUCT REFERENCE - The product must match this image exactly.'
                    });
                });
            } else if (product?.imageUrl) {
                referenceImages.push({
                    url: product.imageUrl,
                    instruction: 'PRODUCT REFERENCE - The product must match this image exactly.'
                });
            }

            // Regenerate the creative
            const result = await generateCreativeFromBrief(modifiedBrief, brand, null, {
                referenceImages,
                aspectRatio: '1:1',
                product,
                logoUrl: brand?.logoUrl || brand?.logo,
                includeLogo: true
            });

            // Determine the new image URL
            const newImageUrl = result.type === 'carousel'
                ? (result.images?.[0]?.url || result.images?.[0]?.imageUrl || result.url)
                : (result.url || result.imageUrl);

            if (!newImageUrl) {
                throw new Error('No image URL returned from generation');
            }

            // Upload if base64
            let finalImageUrl = newImageUrl;
            if (finalImageUrl.startsWith('data:image')) {
                finalImageUrl = await uploadLibraryImage(finalImageUrl, currentUser.uid, selectedBrand?.id);
            }

            // Build carousel images if applicable - filter out undefined values
            let newCarouselImages = null;
            if (result.type === 'carousel' && result.images?.length > 0) {
                newCarouselImages = await Promise.all(result.images.map(async (img) => {
                    let imgUrl = img.url || img.imageUrl;
                    if (imgUrl?.startsWith('data:image')) {
                        imgUrl = await uploadLibraryImage(imgUrl, currentUser.uid, selectedBrand?.id);
                    }
                    // Only include defined properties to avoid Firebase errors
                    const cleanedImg = {};
                    Object.entries(img).forEach(([key, value]) => {
                        if (value !== undefined) {
                            cleanedImg[key] = value;
                        }
                    });
                    cleanedImg.url = imgUrl;
                    cleanedImg.imageUrl = imgUrl;
                    return cleanedImg;
                }));
            }

            // Update library item - only include defined values
            if (selectedResult.libraryItemId) {
                const primaryImageUrl = newCarouselImages?.[0]?.imageUrl || finalImageUrl;
                const updateData = {
                    'metadata.regeneratedAt': new Date().toISOString(),
                    'metadata.regenerationPrompt': refinementInstruction
                };

                // Only add imageUrl/thumbnailUrl if we have a valid URL
                if (primaryImageUrl) {
                    updateData.imageUrl = primaryImageUrl;
                    updateData.thumbnailUrl = primaryImageUrl;
                }

                // Only add carouselImages if we have them (save at root level for consistency with refinements)
                if (newCarouselImages && newCarouselImages.length > 0) {
                    // Deep clean carousel images to remove any undefined values
                    const cleanedCarouselImages = newCarouselImages.map(card => {
                        const cleaned = {};
                        Object.entries(card).forEach(([key, value]) => {
                            if (value !== undefined && value !== null) {
                                cleaned[key] = value;
                            }
                        });
                        return cleaned;
                    });
                    updateData.carouselImages = cleanedCarouselImages;
                }

                await updateLibraryItem(selectedResult.libraryItemId, updateData, currentUser.uid);
            }

            // Update local results state
            setResults(prev => prev.map(r => {
                if (r.briefId === selectedResult.briefId) {
                    return {
                        ...r,
                        imageUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                        thumbnailUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                        carouselImages: newCarouselImages || r.carouselImages
                    };
                }
                return r;
            }));

            // Update selectedResult
            setSelectedResult(prev => prev ? ({
                ...prev,
                imageUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                thumbnailUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                carouselImages: newCarouselImages || prev.carouselImages
            }) : prev);

            // Update briefs
            if (setBriefs) {
                setBriefs(prev => prev.map(b => {
                    if (b.id === selectedResult.briefId) {
                        return {
                            ...b,
                            generatedImageUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                            generatedThumbnailUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                            ...(newCarouselImages && { carouselImages: newCarouselImages })
                        };
                    }
                    return b;
                }));
            }

            setCarouselIndex(0);
            setRegenerateSuccess(true);
            setRegeneratePrompt('');
            setTimeout(() => setRegenerateSuccess(false), 3000);

        } catch (err) {
            console.error('Regeneration failed:', err);
            setRegenerateError(err.message || 'Failed to regenerate creative');
        } finally {
            setIsRegenerating(false);
        }
    };

    // Handle regeneration with a different product
    const handleRegenerateWithProduct = async (newProductId) => {
        if (!selectedResult || !newProductId || !currentUser?.uid) return;

        const brief = briefs.find(b => b.id === selectedResult.briefId);
        if (!brief) {
            setProductRegenerateError('Brief not found');
            return;
        }

        const newProduct = products?.find(p => p.id === newProductId);
        if (!newProduct) {
            setProductRegenerateError('Product not found');
            return;
        }

        setIsRegeneratingWithProduct(true);
        setProductRegenerateError(null);

        try {
            const briefData = brief.brief || brief;
            const outputType = brief.outputType || briefData.outputType || 'image';

            // Build a modified brief with the new product
            const modifiedBriefData = { ...briefData };

            // Update product reference in the brief
            modifiedBriefData.product = newProduct.name;

            // Update scene description to reference the new product
            if (modifiedBriefData.sceneDescription) {
                modifiedBriefData.sceneDescription = `${modifiedBriefData.sceneDescription}\n\nIMPORTANT: Feature the product "${newProduct.name}" prominently in this image.${newProduct.visualNotes ? ` Product visual details: ${newProduct.visualNotes}` : ''}`;
            }

            // For carousels, update each card's scene description
            if (outputType === 'carousel' && modifiedBriefData.carouselCards?.length > 0) {
                modifiedBriefData.carouselCards = modifiedBriefData.carouselCards.map(card => ({
                    ...card,
                    sceneDescription: card.sceneDescription
                        ? `${card.sceneDescription}\n\nIMPORTANT: Feature the product "${newProduct.name}" prominently.${newProduct.visualNotes ? ` Product visual details: ${newProduct.visualNotes}` : ''}`
                        : `Feature the product "${newProduct.name}" prominently.${newProduct.visualNotes ? ` Product visual details: ${newProduct.visualNotes}` : ''}`
                }));
            }

            const modifiedBrief = {
                ...brief,
                brief: modifiedBriefData,
                productId: newProductId,
                product: newProduct.name,
                outputType
            };

            // Build reference images from brand and the NEW product
            const referenceImages = [];
            if (brand?.visual?.logoUrl) {
                referenceImages.push({
                    url: brand.visual.logoUrl,
                    instruction: 'BRAND LOGO - Include this logo in the image where specified.'
                });
            }
            if (newProduct?.images?.length > 0) {
                newProduct.images.slice(0, 2).forEach(img => {
                    referenceImages.push({
                        url: img.url || img,
                        instruction: 'PRODUCT REFERENCE - The product must match this image exactly.'
                    });
                });
            } else if (newProduct?.imageUrl) {
                referenceImages.push({
                    url: newProduct.imageUrl,
                    instruction: 'PRODUCT REFERENCE - The product must match this image exactly.'
                });
            }

            // Regenerate the creative
            const result = await generateCreativeFromBrief(modifiedBrief, brand, null, {
                referenceImages,
                aspectRatio: '1:1',
                product: newProduct,
                logoUrl: brand?.logoUrl || brand?.logo,
                includeLogo: true
            });

            // Determine the new image URL
            const newImageUrl = result.type === 'carousel'
                ? (result.images?.[0]?.url || result.images?.[0]?.imageUrl || result.url)
                : (result.url || result.imageUrl);

            if (!newImageUrl) {
                throw new Error('No image URL returned from generation');
            }

            // Upload if base64
            let finalImageUrl = newImageUrl;
            if (finalImageUrl.startsWith('data:image')) {
                finalImageUrl = await uploadLibraryImage(finalImageUrl, currentUser.uid, selectedBrand?.id);
            }

            // Build carousel images if applicable
            let newCarouselImages = null;
            if (result.type === 'carousel' && result.images?.length > 0) {
                newCarouselImages = await Promise.all(result.images.map(async (img) => {
                    let imgUrl = img.url || img.imageUrl;
                    if (imgUrl?.startsWith('data:image')) {
                        imgUrl = await uploadLibraryImage(imgUrl, currentUser.uid, selectedBrand?.id);
                    }
                    const cleanedImg = {};
                    Object.entries(img).forEach(([key, value]) => {
                        if (value !== undefined) {
                            cleanedImg[key] = value;
                        }
                    });
                    cleanedImg.url = imgUrl;
                    cleanedImg.imageUrl = imgUrl;
                    return cleanedImg;
                }));
            }

            // Update library item with new product info
            if (selectedResult.libraryItemId) {
                const primaryImageUrl = newCarouselImages?.[0]?.imageUrl || finalImageUrl;
                const updateData = {
                    'metadata.regeneratedAt': new Date().toISOString(),
                    'metadata.regenerationReason': `Product changed to: ${newProduct.name}`,
                    'metadata.productId': newProductId,
                    'metadata.productName': newProduct.name
                };

                if (primaryImageUrl) {
                    updateData.imageUrl = primaryImageUrl;
                    updateData.thumbnailUrl = primaryImageUrl;
                }

                if (newCarouselImages && newCarouselImages.length > 0) {
                    const cleanedCarouselImages = newCarouselImages.map(card => {
                        const cleaned = {};
                        Object.entries(card).forEach(([key, value]) => {
                            if (value !== undefined && value !== null) {
                                cleaned[key] = value;
                            }
                        });
                        return cleaned;
                    });
                    updateData.carouselImages = cleanedCarouselImages;
                }

                await updateLibraryItem(selectedResult.libraryItemId, updateData, currentUser.uid);
            }

            // Update local results state
            setResults(prev => prev.map(r => {
                if (r.briefId === selectedResult.briefId) {
                    return {
                        ...r,
                        imageUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                        thumbnailUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                        carouselImages: newCarouselImages || r.carouselImages
                    };
                }
                return r;
            }));

            // Update selectedResult
            setSelectedResult(prev => prev ? ({
                ...prev,
                imageUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                thumbnailUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                carouselImages: newCarouselImages || prev.carouselImages
            }) : prev);

            // Update briefs with new product
            if (setBriefs) {
                setBriefs(prev => prev.map(b => {
                    if (b.id === selectedResult.briefId) {
                        return {
                            ...b,
                            productId: newProductId,
                            product: newProduct.name,
                            generatedImageUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                            generatedThumbnailUrl: newCarouselImages?.[0]?.imageUrl || finalImageUrl,
                            ...(newCarouselImages && { carouselImages: newCarouselImages }),
                            brief: {
                                ...(b.brief || {}),
                                product: newProduct.name,
                                productId: newProductId
                            }
                        };
                    }
                    return b;
                }));
            }

            setCarouselIndex(0);
            setShowProductSelector(false);
            setSelectedProductId(null);

        } catch (err) {
            console.error('Product regeneration failed:', err);
            setProductRegenerateError(err.message || 'Failed to regenerate with new product');
        } finally {
            setIsRegeneratingWithProduct(false);
        }
    };

    // Get the currently viewed image (respects carousel index)
    const getCurrentImage = useCallback(() => {
        if (!selectedResult) return null;
        const carouselImgs = selectedResult.carouselImages || [];
        if (carouselImgs.length > 1) {
            return carouselImgs[carouselIndex]?.imageUrl || carouselImgs[carouselIndex]?.url || selectedResult.imageUrl;
        }
        return selectedResult.imageUrl;
    }, [selectedResult, carouselIndex]);

    // Handle inpainting with mask
    const handleInpaint = async (maskBase64, customPrompt, customMode) => {
        if (!selectedResult || !currentUser?.uid || !selectedBrand?.id) return;

        const finalMode = customMode || inpaintMode;
        const imageToEdit = getCurrentImage();

        if (!imageToEdit) {
            setInpaintError('No image available to edit');
            return;
        }

        setShowMaskDrawer(false);
        setIsInpainting(true);
        setInpaintError(null);

        try {
            // Call the inpaint API with retry logic for overloaded model
            let response;
            let retries = 0;
            const MAX_RETRIES = 1;

            while (retries <= MAX_RETRIES) {
                response = await fetch('http://localhost:3001/api/inpaint-image', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        imageUrl: imageToEdit,
                        maskBase64,
                        prompt: customPrompt || (finalMode === 'remove' ? 'Remove the masked content and fill naturally' : 'Edit the masked area'),
                        editMode: finalMode,
                        userId: currentUser.uid
                    })
                });

                if (response.ok) break;

                const errorData = await response.json().catch(() => ({}));
                const isOverloaded = response.status === 500 && (
                    errorData.details?.toLowerCase().includes('overloaded') ||
                    errorData.error?.toLowerCase().includes('overloaded')
                );

                if (isOverloaded && retries < MAX_RETRIES) {
                    retries++;
                    console.log(`Model overloaded. Retrying in 60 seconds (Attempt ${retries}/${MAX_RETRIES})...`);
                    setInpaintError(`Model is overloaded. Automatic retry in 60 seconds...`);
                    await new Promise(resolve => setTimeout(resolve, 60000));
                    setInpaintError(null);
                    continue;
                }

                console.error('Inpaint error:', response.status, errorData);
                throw new Error(errorData.details || errorData.error || 'Failed to edit image');
            }

            const data = await response.json();
            console.log('Inpaint response:', data);

            if (!data.editedImageUrl) {
                throw new Error('No edited image URL in response');
            }

            // Check if we're editing a carousel card
            const carouselImgs = selectedResult.carouselImages || [];
            const isEditingCarousel = carouselImgs.length > 1;

            // Update the library item
            const editPrompt = `[${finalMode === 'remove' ? 'Removed' : 'Edited'}] ${customPrompt || 'Masked area'}`;

            if (selectedResult.libraryItemId) {
                if (isEditingCarousel) {
                    // For carousels, track history per card
                    const updatedItem = await addCarouselCardRefinedVersion(
                        selectedResult.libraryItemId,
                        carouselIndex,
                        data.editedImageUrl,
                        editPrompt,
                        currentUser.uid,
                        carouselImgs // Pass existing carousel images from result
                    );
                    if (updatedItem) {
                        setSelectedLibraryItem(updatedItem);
                    }
                } else {
                    // For single images, use addRefinedVersion to track history
                    const updatedItem = await addRefinedVersion(
                        selectedResult.libraryItemId,
                        data.editedImageUrl,
                        editPrompt,
                        currentUser.uid
                    );
                    // Update selected library item with new history
                    if (updatedItem) {
                        setSelectedLibraryItem(updatedItem);
                    }
                }
            }

            if (isEditingCarousel) {
                // Update the specific carousel card
                const updatedCarouselImages = carouselImgs.map((img, idx) => {
                    if (idx === carouselIndex) {
                        return { ...img, imageUrl: data.editedImageUrl, url: data.editedImageUrl };
                    }
                    return img;
                });

                setResults(prev => prev.map(r => {
                    if (r.briefId === selectedResult.briefId) {
                        return { ...r, carouselImages: updatedCarouselImages };
                    }
                    return r;
                }));

                setSelectedResult(prev => ({
                    ...prev,
                    carouselImages: updatedCarouselImages
                }));
            } else {
                // Update single image result
                setResults(prev => prev.map(r => {
                    if (r.briefId === selectedResult.briefId) {
                        return {
                            ...r,
                            imageUrl: data.editedImageUrl,
                            thumbnailUrl: data.editedImageUrl
                        };
                    }
                    return r;
                }));

                setSelectedResult(prev => ({
                    ...prev,
                    imageUrl: data.editedImageUrl,
                    thumbnailUrl: data.editedImageUrl
                }));

                if (setBriefs) {
                    setBriefs(prev => prev.map(b => {
                        if (b.id === selectedResult.briefId) {
                            return {
                                ...b,
                                generatedImageUrl: data.editedImageUrl,
                                generatedThumbnailUrl: data.editedImageUrl
                            };
                        }
                        return b;
                    }));
                }
            }

            setRefinementSuccess(true);
            setTimeout(() => setRefinementSuccess(false), 3000);

        } catch (err) {
            console.error('Inpainting failed:', err);
            setInpaintError(err.message || 'Failed to edit image');
        } finally {
            setIsInpainting(false);
        }
    };

    // Handle background removal
    const handleRemoveBackground = async () => {
        if (!selectedResult || !currentUser?.uid) return;

        const imageToProcess = getCurrentImage();
        if (!imageToProcess) {
            setBgRemovalError('No image available to process');
            return;
        }

        setIsRemovingBackground(true);
        setBgRemovalError(null);

        try {
            const response = await fetch('http://localhost:3001/api/remove-background', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    imageUrl: imageToProcess,
                    userId: currentUser.uid
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                if (response.status === 429) {
                    throw new Error('Rate limit exceeded. Please wait a moment and try again.');
                } else if (response.status === 402) {
                    throw new Error('Insufficient credits. Please add more credits to continue.');
                }
                throw new Error(errorData.error || errorData.details || 'Failed to remove background');
            }

            const data = await response.json();

            if (!data.url) {
                throw new Error('No processed image URL in response');
            }

            // Check if we're processing a carousel card
            const carouselImgs = selectedResult.carouselImages || [];
            const isProcessingCarousel = carouselImgs.length > 1;

            // Update the library item
            if (selectedResult.libraryItemId) {
                if (isProcessingCarousel) {
                    // For carousels, track history per card
                    const updatedItem = await addCarouselCardRefinedVersion(
                        selectedResult.libraryItemId,
                        carouselIndex,
                        data.url,
                        'Background removed',
                        currentUser.uid,
                        carouselImgs // Pass existing carousel images from result
                    );
                    if (updatedItem) {
                        setSelectedLibraryItem(updatedItem);
                    }
                } else {
                    // For single images, use addRefinedVersion to track history
                    const updatedItem = await addRefinedVersion(
                        selectedResult.libraryItemId,
                        data.url,
                        'Background removed',
                        currentUser.uid
                    );
                    // Update selected library item with new history
                    if (updatedItem) {
                        setSelectedLibraryItem(updatedItem);
                    }
                }
            }

            if (isProcessingCarousel) {
                const updatedCarouselImages = carouselImgs.map((img, idx) => {
                    if (idx === carouselIndex) {
                        return { ...img, imageUrl: data.url, url: data.url };
                    }
                    return img;
                });

                setResults(prev => prev.map(r => {
                    if (r.briefId === selectedResult.briefId) {
                        return { ...r, carouselImages: updatedCarouselImages };
                    }
                    return r;
                }));

                setSelectedResult(prev => ({
                    ...prev,
                    carouselImages: updatedCarouselImages
                }));
            } else {
                setResults(prev => prev.map(r => {
                    if (r.briefId === selectedResult.briefId) {
                        return {
                            ...r,
                            imageUrl: data.url,
                            thumbnailUrl: data.url
                        };
                    }
                    return r;
                }));

                setSelectedResult(prev => ({
                    ...prev,
                    imageUrl: data.url,
                    thumbnailUrl: data.url
                }));

                if (setBriefs) {
                    setBriefs(prev => prev.map(b => {
                        if (b.id === selectedResult.briefId) {
                            return {
                                ...b,
                                generatedImageUrl: data.url,
                                generatedThumbnailUrl: data.url
                            };
                        }
                        return b;
                    }));
                }
            }

            setRefinementSuccess(true);
            setTimeout(() => setRefinementSuccess(false), 3000);

        } catch (err) {
            console.error('Background removal failed:', err);
            setBgRemovalError(err.message || 'Failed to remove background');
        } finally {
            setIsRemovingBackground(false);
        }
    };

    // Handle crop completion - save cropped image as a new version
    const handleCropComplete = async (croppedDataUrl) => {
        if (!selectedResult || !currentUser?.uid || !selectedBrand?.id) return;

        setShowCropCanvas(false);

        try {
            // Upload the cropped image
            const finalImageUrl = await uploadLibraryImage(
                croppedDataUrl,
                currentUser.uid,
                selectedBrand.id
            );

            // Check if we're cropping a carousel card
            const carouselImgs = selectedResult.carouselImages || [];
            const isCroppingCarousel = carouselImgs.length > 1;

            // Update the library item
            if (selectedResult.libraryItemId) {
                if (isCroppingCarousel) {
                    // For carousels, track history per card
                    const updatedItem = await addCarouselCardRefinedVersion(
                        selectedResult.libraryItemId,
                        carouselIndex,
                        finalImageUrl,
                        '[Cropped]',
                        currentUser.uid,
                        carouselImgs // Pass existing carousel images from result
                    );
                    if (updatedItem) {
                        setSelectedLibraryItem(updatedItem);
                    }
                } else {
                    // For single images, use addRefinedVersion to track history
                    const updatedItem = await addRefinedVersion(
                        selectedResult.libraryItemId,
                        finalImageUrl,
                        '[Cropped]',
                        currentUser.uid
                    );
                    // Update selected library item with new history
                    if (updatedItem) {
                        setSelectedLibraryItem(updatedItem);
                    }
                }
            }

            if (isCroppingCarousel) {
                const updatedCarouselImages = carouselImgs.map((img, idx) => {
                    if (idx === carouselIndex) {
                        return { ...img, imageUrl: finalImageUrl, url: finalImageUrl };
                    }
                    return img;
                });

                setResults(prev => prev.map(r => {
                    if (r.briefId === selectedResult.briefId) {
                        return { ...r, carouselImages: updatedCarouselImages };
                    }
                    return r;
                }));

                setSelectedResult(prev => ({
                    ...prev,
                    carouselImages: updatedCarouselImages
                }));
            } else {
                setResults(prev => prev.map(r => {
                    if (r.briefId === selectedResult.briefId) {
                        return {
                            ...r,
                            imageUrl: finalImageUrl,
                            thumbnailUrl: finalImageUrl
                        };
                    }
                    return r;
                }));

                setSelectedResult(prev => ({
                    ...prev,
                    imageUrl: finalImageUrl,
                    thumbnailUrl: finalImageUrl
                }));

                if (setBriefs) {
                    setBriefs(prev => prev.map(b => {
                        if (b.id === selectedResult.briefId) {
                            return {
                                ...b,
                                generatedImageUrl: finalImageUrl,
                                generatedThumbnailUrl: finalImageUrl
                            };
                        }
                        return b;
                    }));
                }
            }

            setRefinementSuccess(true);
            setTimeout(() => setRefinementSuccess(false), 3000);

        } catch (err) {
            console.error('Crop failed:', err);
            setRefinementError(err.message || 'Failed to save cropped image');
        }
    };

    // Library picker handlers
    const handleOpenLibraryPicker = useCallback(async (addImagesCallback) => {
        setShowLibraryPicker(true);
        setLibrarySelected([]);
        setLibrarySearch('');
        setAddLibraryImagesCallback(() => addImagesCallback);

        if (!currentUser?.uid || !selectedBrand?.id) return;

        setLibraryLoading(true);
        try {
            const items = await loadLibraryItems(currentUser.uid, selectedBrand.id, {
                type: ['image', 'carousel'],
                orderByField: 'createdAt'
            });
            setLibraryImages(items.filter(item => item.imageUrl));
        } catch (err) {
            console.error('Failed to load library images:', err);
            setLibraryImages([]);
        } finally {
            setLibraryLoading(false);
        }
    }, [currentUser?.uid, selectedBrand?.id]);

    const handleConfirmLibrarySelection = useCallback(() => {
        if (addLibraryImagesCallback && librarySelected.length > 0) {
            addLibraryImagesCallback(librarySelected);
        }
        setShowLibraryPicker(false);
        setLibrarySelected([]);
        setAddLibraryImagesCallback(null);
    }, [addLibraryImagesCallback, librarySelected]);

    const toggleLibraryImage = useCallback((item) => {
        setLibrarySelected(prev => {
            const exists = prev.find(i => i.id === item.id);
            if (exists) return prev.filter(i => i.id !== item.id);
            return [...prev, item];
        });
    }, []);

    // Handle selecting a result to view/edit - fetches library item with history
    const handleSelectResult = useCallback(async (result) => {
        setSelectedResult(result);
        setCarouselIndex(0);
        setRefinementError(null);
        setRefinementSuccess(false);
        setRegeneratePrompt('');
        setRegenerateError(null);
        setRegenerateSuccess(false);
        setSelectedLibraryItem(null);
        // Reset product selector state
        setShowProductSelector(false);
        setSelectedProductId(null);
        setProductRegenerateError(null);
        // Reset caption editing state
        setIsEditingCaption(false);

        // Fetch the library item to get the full history
        if (result.libraryItemId) {
            setIsLoadingLibraryItem(true);
            try {
                const libraryItem = await getLibraryItem(result.libraryItemId);
                if (libraryItem) {
                    setSelectedLibraryItem(libraryItem);
                }
            } catch (err) {
                console.error('Failed to fetch library item:', err);
            } finally {
                setIsLoadingLibraryItem(false);
            }
        }
    }, []);

    // Handle setting active version from history
    const handleSetActiveVersion = useCallback(async (historyIndex) => {
        if (!selectedResult?.libraryItemId || !currentUser?.uid || !selectedLibraryItem?.history) return;

        try {
            const updatedItem = await setActiveVersion(
                selectedResult.libraryItemId,
                historyIndex,
                currentUser.uid
            );

            // Update selected library item
            setSelectedLibraryItem(prev => ({
                ...prev,
                imageUrl: updatedItem.imageUrl
            }));

            // Update selected result
            setSelectedResult(prev => ({
                ...prev,
                imageUrl: updatedItem.imageUrl,
                thumbnailUrl: updatedItem.imageUrl
            }));

            // Update results list
            setResults(prev => prev.map(r => {
                if (r.briefId === selectedResult.briefId) {
                    return {
                        ...r,
                        imageUrl: updatedItem.imageUrl,
                        thumbnailUrl: updatedItem.imageUrl
                    };
                }
                return r;
            }));

            // Update briefs
            if (setBriefs) {
                setBriefs(prev => prev.map(b => {
                    if (b.id === selectedResult.briefId) {
                        return {
                            ...b,
                            generatedImageUrl: updatedItem.imageUrl,
                            generatedThumbnailUrl: updatedItem.imageUrl
                        };
                    }
                    return b;
                }));
            }
        } catch (err) {
            console.error('Failed to set active version:', err);
        }
    }, [selectedResult, selectedLibraryItem, currentUser?.uid, setBriefs]);

    // Handle setting active version for carousel card history
    const handleSetCarouselCardActiveVersion = useCallback(async (historyIndex) => {
        if (!selectedResult?.libraryItemId || !currentUser?.uid) return;

        const carouselImgs = selectedResult.carouselImages || [];
        if (carouselImgs.length <= 1) return;

        // Get the current card's history from the library item
        const currentCard = selectedLibraryItem?.carouselImages?.[carouselIndex];
        if (!currentCard?.history) return;

        try {
            const updatedItem = await setCarouselCardActiveVersion(
                selectedResult.libraryItemId,
                carouselIndex,
                historyIndex,
                currentUser.uid
            );

            // Update selected library item
            setSelectedLibraryItem(updatedItem);

            const newImageUrl = updatedItem.carouselImages[carouselIndex].imageUrl;

            // Update selected result
            setSelectedResult(prev => {
                const updatedCarousel = [...(prev.carouselImages || [])];
                if (updatedCarousel[carouselIndex]) {
                    updatedCarousel[carouselIndex] = {
                        ...updatedCarousel[carouselIndex],
                        imageUrl: newImageUrl,
                        url: newImageUrl
                    };
                }
                return { ...prev, carouselImages: updatedCarousel };
            });

            // Update results list
            setResults(prev => prev.map(r => {
                if (r.briefId === selectedResult.briefId) {
                    const updatedCarousel = [...(r.carouselImages || [])];
                    if (updatedCarousel[carouselIndex]) {
                        updatedCarousel[carouselIndex] = {
                            ...updatedCarousel[carouselIndex],
                            imageUrl: newImageUrl,
                            url: newImageUrl
                        };
                    }
                    return { ...r, carouselImages: updatedCarousel };
                }
                return r;
            }));

            // Update briefs
            if (setBriefs) {
                setBriefs(prev => prev.map(b => {
                    if (b.id === selectedResult.briefId) {
                        const updatedCarousel = [...(b.carouselImages || [])];
                        if (updatedCarousel[carouselIndex]) {
                            updatedCarousel[carouselIndex] = {
                                ...updatedCarousel[carouselIndex],
                                imageUrl: newImageUrl,
                                url: newImageUrl
                            };
                        }
                        return { ...b, carouselImages: updatedCarousel };
                    }
                    return b;
                }));
            }
        } catch (err) {
            console.error('Failed to set carousel card active version:', err);
        }
    }, [selectedResult, selectedLibraryItem, carouselIndex, currentUser?.uid, setBriefs]);

    // ============================================================================
    // BULK SELECTION HELPERS
    // ============================================================================

    // Check if a specific image is selected
    const isImageSelected = useCallback((briefId, cardIndex) => {
        return selectedImages.some(img =>
            img.briefId === briefId &&
            (cardIndex === null || cardIndex === undefined
                ? img.cardIndex === undefined
                : img.cardIndex === cardIndex)
        );
    }, [selectedImages]);

    // Toggle image selection
    const handleToggleImageSelection = useCallback((result, cardIndex) => {
        const imageUrl = cardIndex !== null && cardIndex !== undefined && result.carouselImages?.[cardIndex]
            ? (result.carouselImages[cardIndex].imageUrl || result.carouselImages[cardIndex].url)
            : result.imageUrl;

        const selectionItem = {
            briefId: result.briefId,
            cardIndex: cardIndex !== null && cardIndex !== undefined ? cardIndex : undefined,
            imageUrl,
            libraryItemId: result.libraryItemId
        };

        setSelectedImages(prev => {
            const existingIndex = prev.findIndex(img =>
                img.briefId === result.briefId &&
                (cardIndex === null || cardIndex === undefined
                    ? img.cardIndex === undefined
                    : img.cardIndex === cardIndex)
            );

            if (existingIndex >= 0) {
                return prev.filter((_, i) => i !== existingIndex);
            } else {
                return [...prev, selectionItem];
            }
        });
    }, []);

    // Select all images
    const handleSelectAll = useCallback(() => {
        const allImages = [];
        results.forEach(result => {
            if (!result.success || !result.imageUrl) return;

            if (result.carouselImages?.length > 1) {
                // Add each carousel card
                result.carouselImages.forEach((card, idx) => {
                    allImages.push({
                        briefId: result.briefId,
                        cardIndex: idx,
                        imageUrl: card.imageUrl || card.url,
                        libraryItemId: result.libraryItemId
                    });
                });
            } else {
                // Single image
                allImages.push({
                    briefId: result.briefId,
                    imageUrl: result.imageUrl,
                    libraryItemId: result.libraryItemId
                });
            }
        });
        setSelectedImages(allImages);
    }, [results]);

    // Clear selection
    const handleClearSelection = useCallback(() => {
        setSelectedImages([]);
    }, []);

    // Expand carousel to select individual cards
    const handleExpandCarouselSelection = useCallback((result) => {
        setExpandedCarouselBriefId(prev =>
            prev === result.briefId ? null : result.briefId
        );
    }, []);

    // ============================================================================
    // BULK REFINEMENT LOGIC
    // ============================================================================

    const handleBulkRefine = useCallback(async (prompt, userReferenceImages = [], regenerateFromScratch = false) => {
        if (!prompt?.trim() || !currentUser?.uid || !selectedBrand?.id || selectedImages.length === 0) {
            return;
        }

        setBulkRefinementPrompt(prompt.trim());
        setIsBulkRefining(true);
        setBulkRefinementError(null);
        setBulkRefinementProgress({
            current: 0,
            total: selectedImages.length,
            currentImage: null,
            status: 'processing',
            results: []
        });

        const DELAY_BETWEEN_REQUESTS = 15000; // 15 seconds
        const resultsAccumulator = [];

        for (let i = 0; i < selectedImages.length; i++) {
            const imageItem = selectedImages[i];

            // Update progress - starting this image
            setBulkRefinementProgress(prev => ({
                ...prev,
                currentImage: imageItem
            }));

            try {
                // Add delay between requests (except first)
                if (i > 0) {
                    await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_REQUESTS));
                }

                // Get the actual image URL from current results
                const result = results.find(r => r.briefId === imageItem.briefId);
                const imageToRefine = imageItem.cardIndex !== undefined
                    ? (result?.carouselImages?.[imageItem.cardIndex]?.imageUrl ||
                       result?.carouselImages?.[imageItem.cardIndex]?.url)
                    : result?.imageUrl;

                if (!imageToRefine && !regenerateFromScratch) {
                    throw new Error('Image not found');
                }

                // Filter valid reference images
                const validRefImages = userReferenceImages.filter(img => img.status === 'valid' && img.url);

                // Build reference images array
                const referenceImages = [];
                if (!regenerateFromScratch && imageToRefine) {
                    referenceImages.push({
                        url: imageToRefine,
                        instruction: '[ORIGINAL IMAGE] - Edit this image according to the instructions below.'
                    });
                }
                // Add original product photos so the AI maintains product fidelity
                const brief = briefs.find(b => b.id === imageItem.briefId);
                const briefData = brief?.brief || brief || {};
                const product = products?.find(p =>
                    p.id === brief?.productId || p.name === briefData.product
                ) || null;

                if (product?.images?.length > 0) {
                    product.images.slice(0, 3).forEach((img, idx) => {
                        const imgUrl = img.url || img;
                        if (imgUrl) {
                            referenceImages.push({
                                url: imgUrl,
                                instruction: `[PRODUCT PHOTO ${idx + 1}] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
                            });
                        }
                    });
                } else if (product?.imageUrl) {
                    referenceImages.push({
                        url: product.imageUrl,
                        instruction: `[PRODUCT PHOTO] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
                    });
                }

                // Add brand logo
                const logoUrl = brand?.logoUrl || brand?.logo || brand?.branding?.logoUrl;
                if (logoUrl) {
                    referenceImages.push({
                        url: logoUrl,
                        instruction: `[BRAND LOGO] - HIGH-FIDELITY OBJECT. Reproduce this logo EXACTLY as-is. Do not recolor, distort, or modify it in any way.`
                    });
                }

                validRefImages.forEach((refImg) => {
                    referenceImages.push({
                        url: refImg.url,
                        instruction: `[${refImg.label}] - Use elements from this reference image as directed.`
                    });
                });

                // Build the prompt
                const refImageMentions = validRefImages.length > 0
                    ? `\nReference images provided: ${validRefImages.map(img => img.label).join(', ')}.`
                    : '';
                const refinementPrompt = regenerateFromScratch
                    ? `${prompt.trim()}${refImageMentions}`
                    : `Edit the [ORIGINAL IMAGE].${refImageMentions}\n\n${prompt.trim()}\n\nKeep everything else about the original image unchanged.`;

                // Call the API
                const response = await fetch('http://localhost:3001/api/generate-image-gemini', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        prompt: refinementPrompt,
                        aspectRatio: '1:1',
                        referenceImages,
                        userId: currentUser.uid
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.details || errorData.error || 'Failed to refine image');
                }

                const data = await response.json();
                if (!data.url) {
                    throw new Error('No image URL in response');
                }

                // Upload if base64
                let finalImageUrl = data.url;
                if (finalImageUrl.startsWith('data:image')) {
                    finalImageUrl = await uploadLibraryImage(finalImageUrl, currentUser.uid, selectedBrand.id);
                }

                // Update library item
                if (imageItem.libraryItemId) {
                    if (imageItem.cardIndex !== undefined) {
                        const carouselImages = result?.carouselImages || [];
                        await addCarouselCardRefinedVersion(
                            imageItem.libraryItemId,
                            imageItem.cardIndex,
                            finalImageUrl,
                            prompt.trim(),
                            currentUser.uid,
                            carouselImages
                        );
                    } else {
                        await addRefinedVersion(
                            imageItem.libraryItemId,
                            finalImageUrl,
                            prompt.trim(),
                            currentUser.uid
                        );
                    }
                }

                // Update local results state
                setResults(prev => prev.map(r => {
                    if (r.briefId !== imageItem.briefId) return r;

                    if (imageItem.cardIndex !== undefined) {
                        const updatedCarousel = [...(r.carouselImages || [])];
                        if (updatedCarousel[imageItem.cardIndex]) {
                            updatedCarousel[imageItem.cardIndex] = {
                                ...updatedCarousel[imageItem.cardIndex],
                                imageUrl: finalImageUrl,
                                url: finalImageUrl
                            };
                        }
                        return { ...r, carouselImages: updatedCarousel };
                    } else {
                        return {
                            ...r,
                            imageUrl: finalImageUrl,
                            thumbnailUrl: finalImageUrl
                        };
                    }
                }));

                // Also update the selected image URL so the preview updates
                setSelectedImages(prev => prev.map(img => {
                    if (img.briefId === imageItem.briefId && img.cardIndex === imageItem.cardIndex) {
                        return { ...img, imageUrl: finalImageUrl };
                    }
                    return img;
                }));

                // Track success
                resultsAccumulator.push({
                    briefId: imageItem.briefId,
                    cardIndex: imageItem.cardIndex,
                    success: true,
                    newImageUrl: finalImageUrl
                });

            } catch (error) {
                console.error(`[${new Date().toISOString()}] Bulk refinement failed for image ${i + 1}:`, error);
                resultsAccumulator.push({
                    briefId: imageItem.briefId,
                    cardIndex: imageItem.cardIndex,
                    success: false,
                    error: error.message
                });
            }

            // Update progress
            setBulkRefinementProgress(prev => ({
                ...prev,
                current: i + 1,
                results: [...resultsAccumulator]
            }));
        }

        // Complete
        setIsBulkRefining(false);
        setBulkRefinementProgress(prev => ({
            ...prev,
            status: 'complete',
            currentImage: null
        }));
    }, [selectedImages, results, currentUser?.uid, selectedBrand?.id]);

    // Calculate time estimate (15s delay + ~10s generation per brief)
    const estimatedTime = approvedBriefs.length * 25;
    const estimatedMinutes = Math.ceil(estimatedTime / 60);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* Header */}
            <div className="card" style={{ padding: 'var(--space-4)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h3 style={{ margin: 0 }}>Generate Creatives</h3>
                        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                            {approvedBriefs.length} approved briefs ready for generation
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                        <Clock size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                            ~{estimatedMinutes} min estimated
                        </span>
                    </div>
                </div>
            </div>

            {/* Brief Type Summary - Read-only display */}
            <div className="card" style={{ padding: 'var(--space-4)' }}>
                <h4 style={{ margin: '0 0 var(--space-3) 0' }}>Creative Type Summary</h4>
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-4)',
                    alignItems: 'center'
                }}>
                    {['image', 'video', 'carousel'].map(type => {
                        const count = typeCounts[type];
                        const TypeIcon = OUTPUT_TYPE_ICONS[type];
                        return (
                            <div key={type} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                opacity: count === 0 ? 0.5 : 1
                            }}>
                                <TypeIcon size={16} style={{ color: 'var(--color-primary)' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{count}</span>
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                    {OUTPUT_TYPE_LABELS[type]}s
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Progress Section */}
            {status !== 'idle' && (
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    {/* Background indicator */}
                    {status === 'background' && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-3)',
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'var(--info-50)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--info-200)'
                        }}>
                            <Cloud size={16} style={{ color: 'var(--info-600)' }} />
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--info-700)' }}>
                                Running in background - you can close this modal and continue working
                            </span>
                        </div>
                    )}

                    {/* Progress Bar */}
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 'var(--space-2)'
                        }}>
                            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                                {status === 'generating' ? 'Generating...' :
                                 status === 'background' ? 'Generating in Background...' :
                                 'Generation Complete'}
                            </span>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                {progress.current} / {progress.total}
                            </span>
                        </div>
                        <div style={{
                            height: '8px',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-full)',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                height: '100%',
                                width: `${(progress.current / progress.total) * 100}%`,
                                background: status === 'error' ? 'var(--error-500)' : 'var(--color-primary)',
                                borderRadius: 'var(--radius-full)',
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                    </div>

                    {/* Current Brief / Status */}
                    {(status === 'generating' || status === 'background') && (
                        <div style={{
                            padding: 'var(--space-3)',
                            background: retryInfo ? 'var(--warning-50)' : waitMessage ? 'var(--info-50)' : 'var(--color-bg-secondary)',
                            border: retryInfo ? '1px solid var(--warning-200)' : waitMessage ? '1px solid var(--info-200)' : 'none',
                            borderRadius: 'var(--radius-md)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)'
                        }}>
                            {retryInfo ? (
                                <>
                                    <RefreshCw size={20} className="spin" style={{ color: 'var(--warning-600)' }} />
                                    <div>
                                        <div style={{ fontWeight: 500, color: 'var(--warning-700)' }}>
                                            Rate Limited - Retrying
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--warning-600)' }}>
                                            Attempt {retryInfo.attempt}/{retryInfo.maxAttempts} - waiting {retryInfo.retryInSeconds}s before retry
                                        </div>
                                    </div>
                                </>
                            ) : waitMessage ? (
                                <>
                                    <Clock size={20} style={{ color: 'var(--info-600)' }} />
                                    <div>
                                        <div style={{ fontWeight: 500, color: 'var(--info-700)' }}>
                                            Pacing Requests
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--info-600)' }}>
                                            {waitMessage}
                                        </div>
                                    </div>
                                </>
                            ) : currentBrief ? (
                                <>
                                    <Loader2 size={20} className="spin" style={{ color: 'var(--color-primary)' }} />
                                    <div>
                                        <div style={{ fontWeight: 500 }}>
                                            {currentBrief.brief?.selectedHeadline || `Brief #${currentBrief.sequence}`}
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                            Generating {currentBrief.outputType}...
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Loader2 size={20} className="spin" style={{ color: 'var(--color-primary)' }} />
                                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                        Starting generation...
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Stats */}
                    {(status === 'complete' || status === 'error') && (
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-4)',
                            marginTop: 'var(--space-3)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <CheckCircle size={18} style={{ color: 'var(--success-600)' }} />
                                <span style={{ color: 'var(--success-600)', fontWeight: 500 }}>
                                    {successCount} successful
                                </span>
                            </div>
                            {failedCount > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <XCircle size={18} style={{ color: 'var(--error-600)' }} />
                                    <span style={{ color: 'var(--error-600)', fontWeight: 500 }}>
                                        {failedCount} failed
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div style={{
                    padding: 'var(--space-4)',
                    background: 'var(--error-50)',
                    border: '1px solid var(--error-200)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--space-3)'
                }}>
                    <AlertCircle size={20} style={{ color: 'var(--error-600)', flexShrink: 0 }} />
                    <div>
                        <div style={{ fontWeight: 500, color: 'var(--error-700)' }}>Generation Error</div>
                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--error-600)' }}>{error}</div>
                    </div>
                </div>
            )}

            {/* Results List */}
            {results.length > 0 && (
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    {/* Results Header with Approval Summary */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-2)',
                        flexWrap: 'wrap',
                        gap: 'var(--space-2)'
                    }}>
                        <h4 style={{ margin: 0 }}>Review &amp; Approve</h4>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                            {(status === 'complete' || status === 'error') && successCount > 0 && (
                                <button
                                    onClick={handleApproveAll}
                                    className="btn btn-sm btn-primary"
                                    disabled={approvedCount === successCount}
                                >
                                    <CheckCircle size={14} />
                                    Approve All
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Approval Status Summary */}
                    {(status === 'complete' || status === 'error') && successCount > 0 && (
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-4)',
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-sm)',
                            marginBottom: 'var(--space-3)',
                            fontSize: 'var(--font-size-sm)',
                            flexWrap: 'wrap'
                        }}>
                            {pendingApprovalCount > 0 && (
                                <span style={{ color: 'var(--color-text-secondary)' }}>
                                    {pendingApprovalCount} pending
                                </span>
                            )}
                            {approvedCount > 0 && (
                                <span style={{ color: 'var(--success-600)', fontWeight: 500 }}>
                                    {approvedCount} approved
                                </span>
                            )}
                            {savedCount > 0 && (
                                <span style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                                    {savedCount} saved to library
                                </span>
                            )}
                            {discardedCount > 0 && (
                                <span style={{ color: 'var(--error-600)', fontWeight: 500 }}>
                                    {discardedCount} discarded
                                </span>
                            )}
                        </div>
                    )}

                    {/* Bulk Selection Controls */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        marginBottom: 'var(--space-3)',
                        gap: 'var(--space-2)'
                    }}>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                            {bulkSelectionMode && selectedImages.length > 0 && (
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-primary)',
                                    fontWeight: 500
                                }}>
                                    {selectedImages.length} selected
                                </span>
                            )}
                            {bulkSelectionMode && (
                                <button
                                    onClick={handleSelectAll}
                                    className="btn btn-ghost btn-sm"
                                    title="Select all images"
                                >
                                    <CheckSquare size={14} />
                                    All
                                </button>
                            )}
                            {bulkSelectionMode && selectedImages.length > 0 && (
                                <button
                                    onClick={handleClearSelection}
                                    className="btn btn-ghost btn-sm"
                                    title="Clear selection"
                                >
                                    <Square size={14} />
                                    Clear
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setBulkSelectionMode(!bulkSelectionMode);
                                    if (bulkSelectionMode) {
                                        setSelectedImages([]);
                                        setExpandedCarouselBriefId(null);
                                    }
                                }}
                                className={`btn btn-sm ${bulkSelectionMode ? 'btn-secondary' : 'btn-ghost'}`}
                            >
                                {bulkSelectionMode ? (
                                    <>
                                        <X size={14} />
                                        Cancel
                                    </>
                                ) : (
                                    <>
                                        <CheckSquare size={14} />
                                        Select Multiple
                                    </>
                                )}
                            </button>
                            {bulkSelectionMode && selectedImages.length > 0 && (
                                <button
                                    onClick={() => setShowBulkRefinementModal(true)}
                                    className="btn btn-sm btn-primary"
                                >
                                    <Wand2 size={14} />
                                    Refine {selectedImages.length} Images
                                </button>
                            )}
                        </div>
                    </div>

                    <div style={{
                        maxHeight: '400px',
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2)'
                    }}>
                        {results.map((result, index) => {
                            const brief = briefs.find(b => b.id === result.briefId);
                            const TypeIcon = OUTPUT_TYPE_ICONS[brief?.outputType] || Image;
                            const isCarousel = result.carouselImages?.length > 1;
                            const isExpanded = expandedCarouselBriefId === result.briefId;
                            const approvalStatus = approvalStatuses[result.briefId] || 'pending';

                            // Approval-aware row styling
                            const rowBackground = !result.success ? 'var(--error-50)'
                                : approvalStatus === 'approved' ? 'var(--success-50)'
                                : approvalStatus === 'saved' ? 'var(--info-50, var(--color-bg-secondary))'
                                : approvalStatus === 'discarded' ? 'var(--color-bg-secondary)'
                                : 'var(--color-bg-primary)';
                            const rowBorderLeft = !result.success ? 'none'
                                : approvalStatus === 'approved' ? '3px solid var(--success-500)'
                                : approvalStatus === 'saved' ? '3px solid var(--color-primary)'
                                : approvalStatus === 'discarded' ? '3px solid var(--error-300)'
                                : '3px solid transparent';

                            return (
                                <div key={result.briefId || index} style={{ position: 'relative' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-3)',
                                        padding: 'var(--space-2) var(--space-2) var(--space-2) var(--space-3)',
                                        background: rowBackground,
                                        borderRadius: 'var(--radius-sm)',
                                        borderLeft: rowBorderLeft,
                                        opacity: approvalStatus === 'discarded' ? 0.55 : 1,
                                        transition: 'all 0.15s ease'
                                    }}>
                                        {/* Thumbnail with Selection Checkbox */}
                                        {result.success && result.imageUrl ? (
                                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                                {/* Selection checkbox overlay */}
                                                {bulkSelectionMode && (
                                                    <div
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // For carousels, selecting main image selects all cards
                                                            if (isCarousel) {
                                                                // Toggle all carousel cards
                                                                const allSelected = result.carouselImages.every((_, idx) =>
                                                                    isImageSelected(result.briefId, idx)
                                                                );
                                                                if (allSelected) {
                                                                    // Deselect all
                                                                    setSelectedImages(prev =>
                                                                        prev.filter(img => img.briefId !== result.briefId)
                                                                    );
                                                                } else {
                                                                    // Select all cards
                                                                    const newSelections = result.carouselImages.map((card, idx) => ({
                                                                        briefId: result.briefId,
                                                                        cardIndex: idx,
                                                                        imageUrl: card.imageUrl || card.url,
                                                                        libraryItemId: result.libraryItemId
                                                                    }));
                                                                    setSelectedImages(prev => {
                                                                        const filtered = prev.filter(img => img.briefId !== result.briefId);
                                                                        return [...filtered, ...newSelections];
                                                                    });
                                                                }
                                                            } else {
                                                                handleToggleImageSelection(result, null);
                                                            }
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '-6px',
                                                            left: '-6px',
                                                            width: '22px',
                                                            height: '22px',
                                                            borderRadius: '4px',
                                                            background: (isCarousel
                                                                ? result.carouselImages.some((_, idx) => isImageSelected(result.briefId, idx))
                                                                : isImageSelected(result.briefId, null))
                                                                ? 'var(--color-primary)' : 'white',
                                                            border: '2px solid var(--color-primary)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            zIndex: 10,
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                        }}
                                                    >
                                                        {(isCarousel
                                                            ? result.carouselImages.some((_, idx) => isImageSelected(result.briefId, idx))
                                                            : isImageSelected(result.briefId, null)) && (
                                                            <Check size={12} color="white" strokeWidth={3} />
                                                        )}
                                                    </div>
                                                )}
                                                <div
                                                    onClick={() => !bulkSelectionMode && handleSelectResult(result)}
                                                    style={{
                                                        width: '48px',
                                                        height: '48px',
                                                        borderRadius: 'var(--radius-sm)',
                                                        overflow: 'hidden',
                                                        cursor: bulkSelectionMode ? 'default' : 'pointer',
                                                        border: '2px solid var(--success-300)'
                                                    }}
                                                >
                                                    <img
                                                        src={result.thumbnailUrl || result.imageUrl}
                                                        alt="Generated"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                                {/* Carousel indicator for expanding individual selection */}
                                                {isCarousel && bulkSelectionMode && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleExpandCarouselSelection(result);
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: '-6px',
                                                            right: '-6px',
                                                            background: isExpanded ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                                                            border: '1px solid var(--color-border)',
                                                            borderRadius: '4px',
                                                            padding: '2px 4px',
                                                            fontSize: '10px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '2px',
                                                            color: isExpanded ? 'white' : 'var(--color-text-secondary)',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                                                        }}
                                                        title="Select individual carousel cards"
                                                    >
                                                        <Layers size={10} />
                                                        {result.carouselImages.length}
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{
                                                width: '48px',
                                                height: '48px',
                                                borderRadius: 'var(--radius-sm)',
                                                background: result.success ? 'var(--success-100)' : 'var(--error-100)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                {result.success ? (
                                                    <CheckCircle size={20} style={{ color: 'var(--success-600)' }} />
                                                ) : (
                                                    <XCircle size={20} style={{ color: 'var(--error-600)' }} />
                                                )}
                                            </div>
                                        )}
                                        <TypeIcon size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <span style={{
                                                fontSize: 'var(--font-size-sm)',
                                                color: result.success
                                                    ? (approvalStatus === 'discarded' ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)')
                                                    : 'var(--error-700)',
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                textDecoration: approvalStatus === 'discarded' ? 'line-through' : 'none'
                                            }}>
                                                {brief?.brief?.selectedHeadline || `Brief #${brief?.sequence || index + 1}`}
                                            </span>
                                            {result.error && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--error-600)'
                                                }}>
                                                    {result.error}
                                                </span>
                                            )}
                                            {result.success && !bulkSelectionMode && approvalStatus === 'pending' && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-secondary)'
                                                }}>
                                                    Click to view &bull; Choose an action
                                                </span>
                                            )}
                                            {result.success && !bulkSelectionMode && approvalStatus === 'approved' && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--success-600)',
                                                    fontWeight: 500
                                                }}>
                                                    Approved for scheduling
                                                </span>
                                            )}
                                            {result.success && !bulkSelectionMode && approvalStatus === 'saved' && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-primary)',
                                                    fontWeight: 500
                                                }}>
                                                    Saved to library
                                                </span>
                                            )}
                                            {result.success && !bulkSelectionMode && approvalStatus === 'discarded' && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--error-500)'
                                                }}>
                                                    Will be discarded
                                                </span>
                                            )}
                                            {result.success && bulkSelectionMode && isCarousel && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-secondary)'
                                                }}>
                                                    {result.carouselImages.filter((_, idx) => isImageSelected(result.briefId, idx)).length} of {result.carouselImages.length} cards selected
                                                </span>
                                            )}
                                        </div>
                                        {/* Approval action buttons + Refine - hidden in selection mode */}
                                        {result.success && result.imageUrl && !bulkSelectionMode && (
                                            <div style={{ display: 'flex', gap: '2px', alignItems: 'center', flexShrink: 0 }}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleApprove(result.briefId); }}
                                                    style={{
                                                        background: approvalStatus === 'approved' ? 'var(--success-100)' : 'transparent',
                                                        border: 'none',
                                                        borderRadius: 'var(--radius-sm)',
                                                        padding: '6px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: approvalStatus === 'approved' ? 'var(--success-600)' : 'var(--color-text-tertiary)',
                                                        transition: 'all 0.15s ease'
                                                    }}
                                                    title={approvalStatus === 'approved' ? 'Undo approval' : 'Approve for scheduling'}
                                                >
                                                    <CheckCircle size={16} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleSaveToLibrary(result.briefId); }}
                                                    style={{
                                                        background: approvalStatus === 'saved' ? 'var(--info-100, var(--color-bg-secondary))' : 'transparent',
                                                        border: 'none',
                                                        borderRadius: 'var(--radius-sm)',
                                                        padding: '6px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: approvalStatus === 'saved' ? 'var(--color-primary)' : 'var(--color-text-tertiary)',
                                                        transition: 'all 0.15s ease'
                                                    }}
                                                    title={approvalStatus === 'saved' ? 'Undo save to library' : 'Save to library only'}
                                                >
                                                    <BookmarkPlus size={16} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDiscardResult(result.briefId); }}
                                                    style={{
                                                        background: approvalStatus === 'discarded' ? 'var(--error-100)' : 'transparent',
                                                        border: 'none',
                                                        borderRadius: 'var(--radius-sm)',
                                                        padding: '6px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: approvalStatus === 'discarded' ? 'var(--error-600)' : 'var(--color-text-tertiary)',
                                                        transition: 'all 0.15s ease'
                                                    }}
                                                    title={approvalStatus === 'discarded' ? 'Undo discard' : 'Discard'}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                                <div style={{ width: '1px', height: '20px', background: 'var(--color-border)', margin: '0 2px' }} />
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectResult(result);
                                                    }}
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        borderRadius: 'var(--radius-sm)',
                                                        padding: '6px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'var(--color-text-tertiary)',
                                                        transition: 'all 0.15s ease'
                                                    }}
                                                    title="View & refine"
                                                >
                                                    <Wand2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Expanded Carousel Card Selection */}
                                    {isExpanded && bulkSelectionMode && isCarousel && (
                                        <div style={{
                                            marginTop: 'var(--space-2)',
                                            marginLeft: '56px',
                                            padding: 'var(--space-2)',
                                            background: 'var(--color-bg-secondary)',
                                            borderRadius: 'var(--radius-md)',
                                            display: 'flex',
                                            gap: 'var(--space-2)',
                                            overflowX: 'auto'
                                        }}>
                                            {result.carouselImages.map((card, cardIdx) => {
                                                const cardSelected = isImageSelected(result.briefId, cardIdx);
                                                return (
                                                    <div
                                                        key={cardIdx}
                                                        onClick={() => handleToggleImageSelection(result, cardIdx)}
                                                        style={{
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                            borderRadius: 'var(--radius-sm)',
                                                            overflow: 'hidden',
                                                            border: cardSelected
                                                                ? '2px solid var(--color-primary)'
                                                                : '2px solid var(--color-border)',
                                                            flexShrink: 0,
                                                            transition: 'border-color 0.15s ease'
                                                        }}
                                                    >
                                                        <img
                                                            src={card.imageUrl || card.url}
                                                            alt={`Card ${cardIdx + 1}`}
                                                            style={{
                                                                width: '56px',
                                                                height: '56px',
                                                                objectFit: 'cover',
                                                                display: 'block'
                                                            }}
                                                        />
                                                        {/* Selection indicator */}
                                                        {cardSelected && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '2px',
                                                                right: '2px',
                                                                background: 'var(--color-primary)',
                                                                borderRadius: '50%',
                                                                width: '16px',
                                                                height: '16px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <Check size={10} color="white" strokeWidth={3} />
                                                            </div>
                                                        )}
                                                        {/* Card number */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            background: 'rgba(0,0,0,0.6)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            textAlign: 'center',
                                                            padding: '2px'
                                                        }}>
                                                            {cardIdx + 1}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Actions */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 'var(--space-4)',
                borderTop: '1px solid var(--color-border)'
            }}>
                <button onClick={onBack} className="btn btn-ghost">
                    Back
                </button>

                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {status === 'idle' && (
                        <button
                            onClick={startBackgroundJob}
                            disabled={approvedBriefs.length === 0}
                            className="btn btn-primary"
                            title="Generation runs in the background - you can close this modal and check progress in the sidebar"
                        >
                            <Play size={16} />
                            Start Generation
                        </button>
                    )}

                    {status === 'background' && (
                        <>
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)'
                            }}>
                                <Info size={14} />
                                You can close this modal - check progress in the sidebar
                            </span>
                            <button
                                onClick={handleCancelBackground}
                                className="btn btn-ghost"
                                style={{ color: 'var(--error-600)' }}
                            >
                                <CloudOff size={16} />
                                Cancel
                            </button>
                            <button className="btn btn-secondary" disabled>
                                <Loader2 size={16} className="spin" />
                                Running in Background...
                            </button>
                        </>
                    )}

                    {(status === 'complete' || status === 'error') && failedCount > 0 && (
                        <button onClick={retryFailed} className="btn btn-secondary">
                            <RefreshCw size={16} />
                            Retry
                        </button>
                    )}

                    {(status === 'complete' || status === 'error') && successCount > 0 && (
                        <button
                            onClick={handleComplete}
                            className="btn btn-primary"
                            disabled={approvedCount === 0 || isFinalizingApprovals}
                            title={approvedCount === 0
                                ? 'Approve at least one creative to continue'
                                : `Continue with ${approvedCount} approved creative${approvedCount > 1 ? 's' : ''}`
                            }
                        >
                            {isFinalizingApprovals ? (
                                <Loader2 size={16} className="spin" />
                            ) : (
                                <ArrowRight size={16} />
                            )}
                            {isFinalizingApprovals
                                ? 'Finalizing...'
                                : approvedCount > 0
                                    ? `Continue with ${approvedCount} Creative${approvedCount > 1 ? 's' : ''}`
                                    : 'Continue'
                            }
                        </button>
                    )}
                </div>
            </div>

            {/* Creative Detail & Refinement Side Panel */}
            {selectedResult && (() => {
                const selectedBrief = briefs.find(b => b.id === selectedResult.briefId);
                const briefData = selectedBrief?.brief || {};
                const caption = briefData.caption || briefData.description || '';
                const hashtags = briefData.hashtags || [];
                const headline = briefData.selectedHeadline || '';
                const carouselImages = selectedResult.carouselImages || [];
                const isCarousel = carouselImages.length > 1;
                const currentImage = isCarousel
                    ? (carouselImages[carouselIndex]?.imageUrl || carouselImages[carouselIndex]?.url || selectedResult.imageUrl)
                    : selectedResult.imageUrl;

                return (
                    <div
                        onClick={() => {
                            if (!showMaskDrawer && !showCropCanvas) {
                                setSelectedResult(null);
                                setSelectedLibraryItem(null);
                                setShowFullscreen(false);
                                setShowProductSelector(false);
                                setProductRegenerateError(null);
                            }
                        }}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0, 0, 0, 0.4)',
                            zIndex: 10000
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                width: '480px',
                                maxWidth: '100vw',
                                background: 'var(--color-bg-primary)',
                                borderLeft: '1px solid var(--color-border)',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)'
                            }}
                        >
                            {/* Header with Navigation */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--space-3) var(--space-4)',
                                borderBottom: '1px solid var(--color-border)',
                                flexShrink: 0
                            }}>
                                {/* Left: Navigation arrows */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <button
                                        onClick={() => {
                                            const successResults = results.filter(r => r.success && r.imageUrl);
                                            const currentIdx = successResults.findIndex(r => r.briefId === selectedResult.briefId);
                                            if (currentIdx > 0) {
                                                handleSelectResult(successResults[currentIdx - 1]);
                                            }
                                        }}
                                        disabled={(() => {
                                            const successResults = results.filter(r => r.success && r.imageUrl);
                                            return successResults.findIndex(r => r.briefId === selectedResult.briefId) <= 0;
                                        })()}
                                        style={{
                                            background: 'var(--color-bg-secondary)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-sm)',
                                            color: 'var(--color-text-primary)',
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            opacity: (() => {
                                                const successResults = results.filter(r => r.success && r.imageUrl);
                                                return successResults.findIndex(r => r.briefId === selectedResult.briefId) <= 0 ? 0.4 : 1;
                                            })()
                                        }}
                                        title="Previous creative"
                                    >
                                        <ChevronLeft size={18} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            const successResults = results.filter(r => r.success && r.imageUrl);
                                            const currentIdx = successResults.findIndex(r => r.briefId === selectedResult.briefId);
                                            if (currentIdx < successResults.length - 1) {
                                                handleSelectResult(successResults[currentIdx + 1]);
                                            }
                                        }}
                                        disabled={(() => {
                                            const successResults = results.filter(r => r.success && r.imageUrl);
                                            const currentIdx = successResults.findIndex(r => r.briefId === selectedResult.briefId);
                                            return currentIdx >= successResults.length - 1;
                                        })()}
                                        style={{
                                            background: 'var(--color-bg-secondary)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-sm)',
                                            color: 'var(--color-text-primary)',
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            opacity: (() => {
                                                const successResults = results.filter(r => r.success && r.imageUrl);
                                                const currentIdx = successResults.findIndex(r => r.briefId === selectedResult.briefId);
                                                return currentIdx >= successResults.length - 1 ? 0.4 : 1;
                                            })()
                                        }}
                                        title="Next creative"
                                    >
                                        <ChevronRight size={18} />
                                    </button>
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                        {results.filter(r => r.success && r.imageUrl).findIndex(r => r.briefId === selectedResult.briefId) + 1} / {results.filter(r => r.success && r.imageUrl).length}
                                    </span>
                                </div>

                                {/* Center: Title */}
                                <div style={{ minWidth: 0, flex: 1, textAlign: 'center', padding: '0 var(--space-2)' }}>
                                    <h3 style={{ margin: 0, fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {headline || 'Generated Creative'}
                                    </h3>
                                </div>

                                {/* Right: Close button */}
                                <button
                                    onClick={() => {
                                        setSelectedResult(null);
                                        setSelectedLibraryItem(null);
                                        setShowProductSelector(false);
                                        setProductRegenerateError(null);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: 'var(--color-text-secondary)',
                                        padding: 'var(--space-1)',
                                        flexShrink: 0
                                    }}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Selection Bar */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--space-2) var(--space-4)',
                                background: 'var(--color-bg-secondary)',
                                borderBottom: '1px solid var(--color-border)',
                                flexShrink: 0
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    {/* Selection checkbox for current image/card */}
                                    <button
                                        onClick={() => {
                                            if (isCarousel) {
                                                handleToggleImageSelection(selectedResult, carouselIndex);
                                            } else {
                                                handleToggleImageSelection(selectedResult, null);
                                            }
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-1) var(--space-2)',
                                            background: (isCarousel
                                                ? isImageSelected(selectedResult.briefId, carouselIndex)
                                                : isImageSelected(selectedResult.briefId, null))
                                                ? 'var(--color-primary)' : 'var(--color-bg-primary)',
                                            color: (isCarousel
                                                ? isImageSelected(selectedResult.briefId, carouselIndex)
                                                : isImageSelected(selectedResult.briefId, null))
                                                ? 'white' : 'var(--color-text-primary)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-sm)',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500
                                        }}
                                    >
                                        {(isCarousel
                                            ? isImageSelected(selectedResult.briefId, carouselIndex)
                                            : isImageSelected(selectedResult.briefId, null)) ? (
                                            <>
                                                <Check size={14} />
                                                Selected
                                            </>
                                        ) : (
                                            <>
                                                <Square size={14} />
                                                Select {isCarousel ? 'Card' : 'Image'}
                                            </>
                                        )}
                                    </button>

                                    {/* For carousels: Select all cards option */}
                                    {isCarousel && (
                                        <button
                                            onClick={() => {
                                                const allSelected = carouselImages.every((_, idx) =>
                                                    isImageSelected(selectedResult.briefId, idx)
                                                );
                                                if (allSelected) {
                                                    setSelectedImages(prev =>
                                                        prev.filter(img => img.briefId !== selectedResult.briefId)
                                                    );
                                                } else {
                                                    const newSelections = carouselImages.map((card, idx) => ({
                                                        briefId: selectedResult.briefId,
                                                        cardIndex: idx,
                                                        imageUrl: card.imageUrl || card.url,
                                                        libraryItemId: selectedResult.libraryItemId
                                                    }));
                                                    setSelectedImages(prev => {
                                                        const filtered = prev.filter(img => img.briefId !== selectedResult.briefId);
                                                        return [...filtered, ...newSelections];
                                                    });
                                                }
                                            }}
                                            className="btn btn-ghost btn-sm"
                                            style={{ fontSize: 'var(--font-size-xs)' }}
                                        >
                                            {carouselImages.every((_, idx) => isImageSelected(selectedResult.briefId, idx))
                                                ? 'Deselect All Cards'
                                                : 'Select All Cards'}
                                        </button>
                                    )}
                                </div>

                                {/* Selection count and bulk refine button */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    {selectedImages.length > 0 && (
                                        <>
                                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', fontWeight: 500 }}>
                                                {selectedImages.length} selected
                                            </span>
                                            <button
                                                onClick={() => setShowBulkRefinementModal(true)}
                                                className="btn btn-primary btn-sm"
                                                style={{ fontSize: 'var(--font-size-xs)' }}
                                            >
                                                <Wand2 size={12} />
                                                Bulk Refine
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Approval Actions Bar */}
                            {(() => {
                                const panelApprovalStatus = approvalStatuses[selectedResult.briefId] || 'pending';
                                return (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        padding: 'var(--space-2) var(--space-4)',
                                        borderBottom: '1px solid var(--color-border)',
                                        flexShrink: 0,
                                        background: panelApprovalStatus === 'approved' ? 'var(--success-50)'
                                            : panelApprovalStatus === 'saved' ? 'var(--info-50, var(--color-bg-secondary))'
                                            : panelApprovalStatus === 'discarded' ? 'var(--error-50)'
                                            : 'var(--color-bg-primary)'
                                    }}>
                                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginRight: 'auto' }}>
                                            {panelApprovalStatus === 'approved' ? 'Approved for scheduling'
                                                : panelApprovalStatus === 'saved' ? 'Saved to library'
                                                : panelApprovalStatus === 'discarded' ? 'Will be discarded'
                                                : 'Pending review'}
                                        </span>
                                        <button
                                            onClick={() => handleApprove(selectedResult.briefId)}
                                            className={`btn btn-sm ${panelApprovalStatus === 'approved' ? 'btn-success' : 'btn-ghost'}`}
                                            style={{
                                                fontSize: 'var(--font-size-xs)',
                                                ...(panelApprovalStatus === 'approved' ? {
                                                    background: 'var(--success-600)',
                                                    color: 'white',
                                                    borderColor: 'var(--success-600)'
                                                } : {})
                                            }}
                                        >
                                            <CheckCircle size={13} />
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleSaveToLibrary(selectedResult.briefId)}
                                            className={`btn btn-sm ${panelApprovalStatus === 'saved' ? 'btn-primary' : 'btn-ghost'}`}
                                            style={{ fontSize: 'var(--font-size-xs)' }}
                                        >
                                            <BookmarkPlus size={13} />
                                            Library
                                        </button>
                                        <button
                                            onClick={() => handleDiscardResult(selectedResult.briefId)}
                                            className={`btn btn-sm ${panelApprovalStatus === 'discarded' ? 'btn-danger' : 'btn-ghost'}`}
                                            style={{
                                                fontSize: 'var(--font-size-xs)',
                                                ...(panelApprovalStatus === 'discarded' ? {
                                                    background: 'var(--error-600)',
                                                    color: 'white',
                                                    borderColor: 'var(--error-600)'
                                                } : {})
                                            }}
                                        >
                                            <Trash2 size={13} />
                                            Discard
                                        </button>
                                    </div>
                                );
                            })()}

                            {/* Scrollable Content */}
                            <div style={{
                                flex: 1,
                                overflow: 'auto',
                                padding: 'var(--space-4)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--space-3)'
                            }}>
                                {/* Image Preview */}
                                <div style={{
                                    borderRadius: 'var(--radius-lg)',
                                    overflow: 'hidden',
                                    border: '1px solid var(--color-border)',
                                    flexShrink: 0
                                }}>
                                        {/* Image / Carousel */}
                                                <div style={{
                                                    background: 'var(--color-bg-secondary)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    position: 'relative'
                                                }}>
                                                    <img
                                                        src={currentImage}
                                                        alt={`Generated creative${isCarousel ? ` ${carouselIndex + 1}/${carouselImages.length}` : ''}`}
                                                        style={{
                                                            width: '100%',
                                                            height: 'auto',
                                                            display: 'block'
                                                        }}
                                                    />

                                                    {/* Fullscreen Button */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowFullscreen(true);
                                                        }}
                                                        title="View fullscreen"
                                                        style={{
                                                            position: 'absolute',
                                                            top: '8px',
                                                            left: '8px',
                                                            background: 'rgba(0, 0, 0, 0.6)',
                                                            border: 'none',
                                                            borderRadius: 'var(--radius-sm)',
                                                            width: '32px',
                                                            height: '32px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            color: 'white',
                                                            transition: 'background 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
                                                    >
                                                        <Maximize2 size={16} />
                                                    </button>

                                                    {/* Carousel Navigation */}
                                                    {isCarousel && (
                                                        <>
                                                            {/* Left Arrow */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setCarouselIndex(prev => prev > 0 ? prev - 1 : carouselImages.length - 1);
                                                                }}
                                                                style={{
                                                                    position: 'absolute',
                                                                    left: '8px',
                                                                    top: '50%',
                                                                    transform: 'translateY(-50%)',
                                                                    background: 'rgba(0, 0, 0, 0.6)',
                                                                    border: 'none',
                                                                    borderRadius: '50%',
                                                                    width: '36px',
                                                                    height: '36px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    color: 'white'
                                                                }}
                                                            >
                                                                <ChevronLeft size={20} />
                                                            </button>

                                                            {/* Right Arrow */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setCarouselIndex(prev => prev < carouselImages.length - 1 ? prev + 1 : 0);
                                                                }}
                                                                style={{
                                                                    position: 'absolute',
                                                                    right: '8px',
                                                                    top: '50%',
                                                                    transform: 'translateY(-50%)',
                                                                    background: 'rgba(0, 0, 0, 0.6)',
                                                                    border: 'none',
                                                                    borderRadius: '50%',
                                                                    width: '36px',
                                                                    height: '36px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    color: 'white'
                                                                }}
                                                            >
                                                                <ChevronRight size={20} />
                                                            </button>

                                                            {/* Dots Indicator */}
                                                            <div style={{
                                                                position: 'absolute',
                                                                bottom: '12px',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                display: 'flex',
                                                                gap: '6px'
                                                            }}>
                                                                {carouselImages.map((_, idx) => (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setCarouselIndex(idx);
                                                                        }}
                                                                        style={{
                                                                            width: idx === carouselIndex ? '20px' : '8px',
                                                                            height: '8px',
                                                                            borderRadius: '4px',
                                                                            background: idx === carouselIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                                                            border: 'none',
                                                                            cursor: 'pointer',
                                                                            padding: 0,
                                                                            transition: 'all 0.2s ease'
                                                                        }}
                                                                    />
                                                                ))}
                                                            </div>

                                                            {/* Image Counter */}
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '12px',
                                                                right: '12px',
                                                                background: 'rgba(0, 0, 0, 0.6)',
                                                                color: 'white',
                                                                padding: '4px 10px',
                                                                borderRadius: '12px',
                                                                fontSize: 'var(--font-size-xs)',
                                                                fontWeight: 500
                                                            }}>
                                                                {carouselIndex + 1} / {carouselImages.length}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                        {/* Caption & Hashtags Section */}
                                        <div style={{ padding: 'var(--space-3)' }}>
                                            {/* Caption */}
                                            {isEditingCaption && selectedResult?.briefId === selectedBrief?.id ? (
                                                <div style={{ marginBottom: 'var(--space-2)' }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        marginBottom: 'var(--space-1)'
                                                    }}>
                                                        <span style={{
                                                            fontSize: 'var(--font-size-xs)',
                                                            color: 'var(--color-text-tertiary)',
                                                            fontWeight: 500
                                                        }}>
                                                            Edit Caption
                                                        </span>
                                                    </div>
                                                    <textarea
                                                        value={editedCaption}
                                                        onChange={(e) => setEditedCaption(e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            minHeight: '80px',
                                                            padding: 'var(--space-2)',
                                                            fontSize: 'var(--font-size-sm)',
                                                            lineHeight: 1.5,
                                                            border: '1px solid var(--color-primary)',
                                                            borderRadius: 'var(--radius-sm)',
                                                            background: 'var(--color-bg-primary)',
                                                            color: 'var(--color-text-primary)',
                                                            resize: 'vertical',
                                                            fontFamily: 'inherit',
                                                            outline: 'none'
                                                        }}
                                                        autoFocus
                                                    />
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: 'var(--space-2)',
                                                        marginTop: 'var(--space-2)',
                                                        justifyContent: 'flex-end'
                                                    }}>
                                                        <button
                                                            onClick={() => setIsEditingCaption(false)}
                                                            disabled={isSavingCaption}
                                                            style={{
                                                                padding: '4px 12px',
                                                                fontSize: 'var(--font-size-xs)',
                                                                border: '1px solid var(--color-border)',
                                                                borderRadius: 'var(--radius-sm)',
                                                                background: 'transparent',
                                                                color: 'var(--color-text-secondary)',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={() => handleSaveCaption(selectedBrief.id, editedCaption)}
                                                            disabled={isSavingCaption}
                                                            style={{
                                                                padding: '4px 12px',
                                                                fontSize: 'var(--font-size-xs)',
                                                                border: 'none',
                                                                borderRadius: 'var(--radius-sm)',
                                                                background: 'var(--color-primary)',
                                                                color: 'white',
                                                                cursor: isSavingCaption ? 'wait' : 'pointer',
                                                                opacity: isSavingCaption ? 0.7 : 1
                                                            }}
                                                        >
                                                            {isSavingCaption ? 'Saving...' : 'Save'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{
                                                    marginBottom: 'var(--space-2)',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 'var(--space-2)'
                                                }}>
                                                    <div style={{ flex: 1 }}>
                                                        {caption ? (
                                                            <>
                                                                <span style={{ fontWeight: 600, marginRight: 'var(--space-2)' }}>
                                                                    {brand?.name || 'Brand'}
                                                                </span>
                                                                <span style={{ fontSize: 'var(--font-size-sm)', lineHeight: 1.5 }}>
                                                                    {caption}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span style={{
                                                                fontSize: 'var(--font-size-sm)',
                                                                color: 'var(--color-text-tertiary)',
                                                                fontStyle: 'italic'
                                                            }}>
                                                                No caption yet
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            setEditedCaption(caption);
                                                            setIsEditingCaption(true);
                                                        }}
                                                        title="Edit caption"
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            padding: '2px',
                                                            color: 'var(--color-text-tertiary)',
                                                            flexShrink: 0,
                                                            marginTop: '2px'
                                                        }}
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                </div>
                                            )}

                                            {/* Hashtags */}
                                            {hashtags.length > 0 && (
                                                <div style={{
                                                    fontSize: 'var(--font-size-sm)',
                                                    color: 'var(--color-primary)',
                                                    lineHeight: 1.6
                                                }}>
                                                    {hashtags.map((tag, i) => (
                                                        <span key={i} style={{ marginRight: 'var(--space-1)' }}>
                                                            {tag.startsWith('#') ? tag : `#${tag}`}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* No content placeholder - only show when no caption AND no hashtags AND not editing */}
                                            {!caption && hashtags.length === 0 && !isEditingCaption && (
                                                <div style={{
                                                    fontSize: 'var(--font-size-sm)',
                                                    color: 'var(--color-text-tertiary)',
                                                    fontStyle: 'italic'
                                                }}>
                                                    No caption or hashtags generated yet
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Version History - for both single images and carousel cards */}
                                    {(() => {
                                        // Determine which history to show
                                        const historyToShow = isCarousel
                                            ? selectedLibraryItem?.carouselImages?.[carouselIndex]?.history
                                            : selectedLibraryItem?.history;
                                        const currentImageUrl = isCarousel
                                            ? selectedLibraryItem?.carouselImages?.[carouselIndex]?.imageUrl
                                            : selectedLibraryItem?.imageUrl;
                                        const onVersionClick = isCarousel
                                            ? handleSetCarouselCardActiveVersion
                                            : handleSetActiveVersion;

                                        if (!historyToShow || historyToShow.length === 0) return null;

                                        return (
                                            <div style={{
                                                padding: 'var(--space-3)',
                                                background: 'var(--color-bg-secondary)',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--color-border)'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-2)',
                                                    marginBottom: 'var(--space-2)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-tertiary)'
                                                }}>
                                                    <Clock size={12} />
                                                    {isCarousel ? `Card ${carouselIndex + 1} History` : 'Version History'} ({historyToShow.length})
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: 'var(--space-2)',
                                                    overflowX: 'auto',
                                                    paddingBottom: 'var(--space-2)'
                                                }}>
                                                    {historyToShow.map((version, idx) => {
                                                        const isActive = currentImageUrl === version.imageUrl;
                                                        return (
                                                            <div
                                                                key={idx}
                                                                style={{
                                                                    flexShrink: 0,
                                                                    position: 'relative',
                                                                    borderRadius: 'var(--radius-md)',
                                                                    border: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
                                                                    overflow: 'hidden'
                                                                }}
                                                            >
                                                                <img
                                                                    src={version.imageUrl}
                                                                    alt={`Version ${idx + 1}`}
                                                                    style={{
                                                                        width: '56px',
                                                                        height: '56px',
                                                                        objectFit: 'cover',
                                                                        cursor: 'pointer',
                                                                        opacity: isActive ? 1 : 0.7
                                                                    }}
                                                                    onClick={() => onVersionClick(idx)}
                                                                    title={version.prompt || 'Original'}
                                                                />
                                                                {/* Original badge */}
                                                                {version.isOriginal && (
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        bottom: '2px',
                                                                        left: '2px',
                                                                        background: 'rgba(0,0,0,0.7)',
                                                                        color: 'white',
                                                                        fontSize: '7px',
                                                                        padding: '1px 3px',
                                                                        borderRadius: '3px'
                                                                    }}>
                                                                        Original
                                                                    </div>
                                                                )}
                                                                {/* Active indicator */}
                                                                {isActive && (
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        top: '2px',
                                                                        right: '2px',
                                                                        background: 'var(--color-primary)',
                                                                        borderRadius: '50%',
                                                                        width: '8px',
                                                                        height: '8px'
                                                                    }} />
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {isLoadingLibraryItem && (
                                                    <div style={{
                                                        fontSize: 'var(--font-size-xs)',
                                                        color: 'var(--color-text-tertiary)'
                                                    }}>
                                                        Loading...
                                                    </div>
                                                )}
                                                <div style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-tertiary)',
                                                    marginTop: 'var(--space-1)'
                                                }}>
                                                    Click a version to use it
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Success message */}
                                    {refinementSuccess && (
                                        <div style={{
                                            padding: 'var(--space-3)',
                                            background: 'var(--success-50)',
                                            border: '1px solid var(--success-200)',
                                            borderRadius: 'var(--radius-md)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            maxWidth: '500px'
                                        }}>
                                            <CheckCircle size={16} style={{ color: 'var(--success-600)' }} />
                                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--success-700)' }}>
                                                Image refined successfully!
                                            </span>
                                        </div>
                                    )}

                                    {/* Brief Info */}
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 'var(--space-2)',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        {selectedBrief?.funnelStage && (
                                            <span style={{
                                                padding: '2px 8px',
                                                background: 'var(--color-bg-tertiary)',
                                                borderRadius: 'var(--radius-full)'
                                            }}>
                                                {selectedBrief.funnelStage}
                                            </span>
                                        )}
                                        {selectedBrief?.pillarName && (
                                            <span style={{
                                                padding: '2px 8px',
                                                background: 'var(--color-bg-tertiary)',
                                                borderRadius: 'var(--radius-full)'
                                            }}>
                                                {selectedBrief.pillarName}
                                            </span>
                                        )}
                                        {selectedBrief?.outputType && (
                                            <span style={{
                                                padding: '2px 8px',
                                                background: 'var(--color-bg-tertiary)',
                                                borderRadius: 'var(--radius-full)'
                                            }}>
                                                {OUTPUT_TYPE_LABELS[selectedBrief.outputType] || selectedBrief.outputType}
                                            </span>
                                        )}
                                    </div>

                                    {/* Linked Product Section */}
                                    {products && products.length > 0 && (
                                        <div style={{
                                            padding: 'var(--space-3)',
                                            background: 'var(--color-bg-secondary)',
                                            borderRadius: 'var(--radius-md)',
                                            border: '1px solid var(--color-border)'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-2)',
                                                marginBottom: 'var(--space-2)'
                                            }}>
                                                <Package size={16} style={{ color: 'var(--color-primary)' }} />
                                                <h4 style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Linked Product</h4>
                                            </div>

                                            {/* Current Product Display */}
                                            {(() => {
                                                const currentProductId = selectedBrief?.productId || selectedBrief?.brief?.productId;
                                                const currentProductName = selectedBrief?.product || selectedBrief?.brief?.product;
                                                const linkedProduct = currentProductId
                                                    ? products.find(p => p.id === currentProductId)
                                                    : currentProductName
                                                        ? products.find(p => p.name === currentProductName)
                                                        : null;

                                                return (
                                                    <div>
                                                        {linkedProduct ? (
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 'var(--space-3)',
                                                                padding: 'var(--space-2)',
                                                                background: 'var(--color-bg-primary)',
                                                                borderRadius: 'var(--radius-sm)',
                                                                border: '1px solid var(--color-border)',
                                                                marginBottom: 'var(--space-2)'
                                                            }}>
                                                                {(linkedProduct.imageUrl || linkedProduct.images?.[0]?.url || linkedProduct.images?.[0]) && (
                                                                    <img
                                                                        src={linkedProduct.imageUrl || linkedProduct.images?.[0]?.url || linkedProduct.images?.[0]}
                                                                        alt={linkedProduct.name}
                                                                        style={{
                                                                            width: '48px',
                                                                            height: '48px',
                                                                            objectFit: 'cover',
                                                                            borderRadius: 'var(--radius-sm)',
                                                                            flexShrink: 0
                                                                        }}
                                                                    />
                                                                )}
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <div style={{
                                                                        fontWeight: 600,
                                                                        fontSize: 'var(--font-size-sm)',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {linkedProduct.name}
                                                                    </div>
                                                                    {linkedProduct.price && (
                                                                        <div style={{
                                                                            fontSize: 'var(--font-size-xs)',
                                                                            color: 'var(--color-text-secondary)'
                                                                        }}>
                                                                            {linkedProduct.price}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{
                                                                padding: 'var(--space-2)',
                                                                background: 'var(--color-bg-primary)',
                                                                borderRadius: 'var(--radius-sm)',
                                                                border: '1px dashed var(--color-border)',
                                                                marginBottom: 'var(--space-2)',
                                                                fontSize: 'var(--font-size-sm)',
                                                                color: 'var(--color-text-tertiary)',
                                                                textAlign: 'center'
                                                            }}>
                                                                No product linked
                                                            </div>
                                                        )}

                                                        {/* Change Product Button / Dropdown */}
                                                        <div style={{ position: 'relative' }}>
                                                            <button
                                                                onClick={() => setShowProductSelector(!showProductSelector)}
                                                                disabled={isRegeneratingWithProduct}
                                                                className="btn btn-secondary"
                                                                style={{
                                                                    width: '100%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    padding: 'var(--space-2) var(--space-3)',
                                                                    fontSize: 'var(--font-size-sm)'
                                                                }}
                                                            >
                                                                <span>{linkedProduct ? 'Change Product & Regenerate' : 'Link Product & Generate'}</span>
                                                                <ChevronDown
                                                                    size={16}
                                                                    style={{
                                                                        transform: showProductSelector ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                        transition: 'transform 0.2s ease'
                                                                    }}
                                                                />
                                                            </button>

                                                            {/* Product Selector Dropdown */}
                                                            {showProductSelector && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: 0,
                                                                    right: 0,
                                                                    marginTop: 'var(--space-1)',
                                                                    background: 'var(--color-bg-primary)',
                                                                    border: '1px solid var(--color-border)',
                                                                    borderRadius: 'var(--radius-md)',
                                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                                    zIndex: 100,
                                                                    maxHeight: '240px',
                                                                    overflowY: 'auto'
                                                                }}>
                                                                    {products.map(product => {
                                                                        const isCurrentProduct = linkedProduct?.id === product.id;
                                                                        const productImage = product.imageUrl || product.images?.[0]?.url || product.images?.[0];

                                                                        return (
                                                                            <button
                                                                                key={product.id}
                                                                                onClick={() => {
                                                                                    if (!isCurrentProduct) {
                                                                                        handleRegenerateWithProduct(product.id);
                                                                                    }
                                                                                    setShowProductSelector(false);
                                                                                }}
                                                                                disabled={isRegeneratingWithProduct}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 'var(--space-2)',
                                                                                    padding: 'var(--space-2) var(--space-3)',
                                                                                    background: isCurrentProduct ? 'var(--color-bg-tertiary)' : 'transparent',
                                                                                    border: 'none',
                                                                                    borderBottom: '1px solid var(--color-border)',
                                                                                    cursor: isCurrentProduct ? 'default' : 'pointer',
                                                                                    textAlign: 'left',
                                                                                    transition: 'background 0.15s ease'
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    if (!isCurrentProduct) {
                                                                                        e.currentTarget.style.background = 'var(--color-bg-secondary)';
                                                                                    }
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.background = isCurrentProduct ? 'var(--color-bg-tertiary)' : 'transparent';
                                                                                }}
                                                                            >
                                                                                {productImage && (
                                                                                    <img
                                                                                        src={productImage}
                                                                                        alt={product.name}
                                                                                        style={{
                                                                                            width: '36px',
                                                                                            height: '36px',
                                                                                            objectFit: 'cover',
                                                                                            borderRadius: 'var(--radius-sm)',
                                                                                            flexShrink: 0
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                                    <div style={{
                                                                                        fontWeight: 500,
                                                                                        fontSize: 'var(--font-size-sm)',
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                        color: 'var(--color-text-primary)'
                                                                                    }}>
                                                                                        {product.name}
                                                                                    </div>
                                                                                    {product.price && (
                                                                                        <div style={{
                                                                                            fontSize: 'var(--font-size-xs)',
                                                                                            color: 'var(--color-text-tertiary)'
                                                                                        }}>
                                                                                            {product.price}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                {isCurrentProduct && (
                                                                                    <span style={{
                                                                                        fontSize: 'var(--font-size-xs)',
                                                                                        color: 'var(--color-primary)',
                                                                                        fontWeight: 500
                                                                                    }}>
                                                                                        Current
                                                                                    </span>
                                                                                )}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Regeneration Status */}
                                                        {isRegeneratingWithProduct && (
                                                            <div style={{
                                                                marginTop: 'var(--space-2)',
                                                                padding: 'var(--space-2)',
                                                                background: 'var(--info-50)',
                                                                border: '1px solid var(--info-200)',
                                                                borderRadius: 'var(--radius-sm)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 'var(--space-2)',
                                                                fontSize: 'var(--font-size-sm)',
                                                                color: 'var(--info-700)'
                                                            }}>
                                                                <Loader2 size={14} className="spin" />
                                                                Regenerating with new product...
                                                            </div>
                                                        )}

                                                        {productRegenerateError && (
                                                            <div style={{
                                                                marginTop: 'var(--space-2)',
                                                                padding: 'var(--space-2)',
                                                                background: 'var(--error-50)',
                                                                border: '1px solid var(--error-100)',
                                                                borderRadius: 'var(--radius-sm)',
                                                                fontSize: 'var(--font-size-xs)',
                                                                color: 'var(--error-600)'
                                                            }}>
                                                                {productRegenerateError}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            <p style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-tertiary)',
                                                margin: 'var(--space-2) 0 0 0'
                                            }}>
                                                Selecting a different product will regenerate this creative featuring the new product.
                                            </p>
                                        </div>
                                    )}

                                    {/* Refine with AI */}
                                    <div style={{
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            <Wand2 size={16} style={{ color: 'var(--color-primary)' }} />
                                            <h4 style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Refine with AI</h4>
                                        </div>

                                        <ImageRefinementPanel
                                            onRefine={handleRefineCreative}
                                            isRefining={isRefining}
                                            error={refinementError}
                                            compact
                                            userId={currentUser?.uid}
                                            brandId={selectedBrand?.id}
                                            originalImageUrl={getCurrentImage()}
                                            originalPrompt={briefData.sceneDescription}
                                            onBrowseLibrary={handleOpenLibraryPicker}
                                            onObjectEdit={() => {
                                                setInpaintMode('replace');
                                                setShowMaskDrawer(true);
                                            }}
                                            onRemoveObject={() => {
                                                setInpaintMode('remove');
                                                setShowMaskDrawer(true);
                                            }}
                                            onCrop={() => setShowCropCanvas(true)}
                                            onRemoveBackground={handleRemoveBackground}
                                            isInpainting={isInpainting}
                                            isRemovingBackground={isRemovingBackground}
                                            bgRemovalError={bgRemovalError}
                                            inpaintError={inpaintError}
                                            showRegenerateOption={true}
                                        />
                                    </div>

                                    {/* Regenerate Creative */}
                                    <div style={{
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            <RefreshCw size={16} style={{ color: 'var(--warning-600)' }} />
                                            <h4 style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                                Regenerate {isCarousel ? 'All Cards' : OUTPUT_TYPE_LABELS[selectedBrief?.outputType] || 'Creative'}
                                            </h4>
                                        </div>
                                        <p style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)',
                                            margin: '0 0 var(--space-2) 0'
                                        }}>
                                            {isCarousel
                                                ? 'Regenerate all carousel cards from scratch with additional direction. This replaces all current cards.'
                                                : 'Regenerate this creative from scratch with additional direction. This replaces the current image.'}
                                        </p>
                                        <textarea
                                            value={regeneratePrompt}
                                            onChange={(e) => setRegeneratePrompt(e.target.value)}
                                            placeholder={isCarousel
                                                ? 'e.g. "Use warmer tones, show the product being used outdoors, make the text overlays larger"'
                                                : 'e.g. "Make the background darker, show the product from a different angle"'}
                                            disabled={isRegenerating}
                                            rows={3}
                                            style={{
                                                width: '100%',
                                                padding: 'var(--space-2)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--font-size-sm)',
                                                resize: 'vertical',
                                                background: 'var(--color-bg-primary)',
                                                color: 'var(--color-text-primary)',
                                                fontFamily: 'inherit'
                                            }}
                                        />
                                        <button
                                            onClick={handleRegenerate}
                                            disabled={isRegenerating || !regeneratePrompt.trim()}
                                            className="btn btn-secondary"
                                            style={{
                                                marginTop: 'var(--space-2)',
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 'var(--space-2)'
                                            }}
                                        >
                                            {isRegenerating ? (
                                                <>
                                                    <Loader2 size={14} className="spin" />
                                                    Regenerating{isCarousel ? ` ${carouselImages.length} cards` : ''}...
                                                </>
                                            ) : (
                                                <>
                                                    <RefreshCw size={14} />
                                                    Regenerate {isCarousel ? 'All Cards' : OUTPUT_TYPE_LABELS[selectedBrief?.outputType] || 'Creative'}
                                                </>
                                            )}
                                        </button>

                                        {regenerateSuccess && (
                                            <div style={{
                                                marginTop: 'var(--space-2)',
                                                padding: 'var(--space-2)',
                                                background: 'var(--success-50)',
                                                border: '1px solid var(--success-200)',
                                                borderRadius: 'var(--radius-sm)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-2)',
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--success-700)'
                                            }}>
                                                <CheckCircle size={14} />
                                                Regenerated successfully!
                                            </div>
                                        )}

                                        {regenerateError && (
                                            <div style={{
                                                marginTop: 'var(--space-2)',
                                                padding: 'var(--space-2)',
                                                background: 'var(--error-50)',
                                                border: '1px solid var(--error-100)',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--error-600)'
                                            }}>
                                                {regenerateError}
                                            </div>
                                        )}
                                    </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Fullscreen Creative Preview Modal */}
            {showFullscreen && selectedResult && (() => {
                const fsCarouselImages = selectedResult.carouselImages || [];
                const fsIsCarousel = fsCarouselImages.length > 1;
                const fsCurrentImage = fsIsCarousel
                    ? (fsCarouselImages[carouselIndex]?.imageUrl || fsCarouselImages[carouselIndex]?.url || selectedResult.imageUrl)
                    : selectedResult.imageUrl;

                return (
                    <div
                        onClick={() => setShowFullscreen(false)}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0, 0, 0, 0.92)',
                            zIndex: 20000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'zoom-out'
                        }}
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setShowFullscreen(false)}
                            style={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                background: 'rgba(255, 255, 255, 0.15)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: 'white',
                                zIndex: 1,
                                transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                        >
                            <X size={22} />
                        </button>

                        {/* Fullscreen Image */}
                        <img
                            src={fsCurrentImage}
                            alt="Creative fullscreen preview"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                maxWidth: '92vw',
                                maxHeight: '92vh',
                                objectFit: 'contain',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'default'
                            }}
                        />

                        {/* Carousel Navigation in Fullscreen */}
                        {fsIsCarousel && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCarouselIndex(prev => prev > 0 ? prev - 1 : fsCarouselImages.length - 1);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        left: '24px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: 'white',
                                        transition: 'background 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                                >
                                    <ChevronLeft size={24} />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCarouselIndex(prev => prev < fsCarouselImages.length - 1 ? prev + 1 : 0);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        right: '24px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: 'white',
                                        transition: 'background 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                                >
                                    <ChevronRight size={24} />
                                </button>

                                {/* Dots */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: '32px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    gap: '8px'
                                }}>
                                    {fsCarouselImages.map((_, idx) => (
                                        <button
                                            key={idx}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCarouselIndex(idx);
                                            }}
                                            style={{
                                                width: idx === carouselIndex ? '24px' : '10px',
                                                height: '10px',
                                                borderRadius: '5px',
                                                background: idx === carouselIndex ? 'white' : 'rgba(255, 255, 255, 0.4)',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: 0,
                                                transition: 'all 0.2s ease'
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Counter */}
                                <div style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    padding: '6px 16px',
                                    borderRadius: '16px',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500
                                }}>
                                    {carouselIndex + 1} / {fsCarouselImages.length}
                                </div>
                            </>
                        )}
                    </div>
                );
            })()}

            {/* Mask Drawing Canvas for Paint-to-Edit */}
            {showMaskDrawer && getCurrentImage() && (
                <MaskDrawingCanvas
                    imageUrl={getCurrentImage()}
                    onMaskComplete={handleInpaint}
                    onCancel={() => setShowMaskDrawer(false)}
                    initialMode={inpaintMode}
                />
            )}

            {/* Image Crop Canvas */}
            {showCropCanvas && getCurrentImage() && (
                <ImageCropCanvas
                    imageUrl={getCurrentImage()}
                    onCropComplete={handleCropComplete}
                    onCancel={() => setShowCropCanvas(false)}
                />
            )}

            {/* Library Image Picker Modal */}
            {showLibraryPicker && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.6)',
                        zIndex: 30000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowLibraryPicker(false);
                            setAddLibraryImagesCallback(null);
                        }
                    }}
                >
                    <div style={{
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-xl)',
                        width: '90%',
                        maxWidth: '720px',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 'var(--space-4)',
                            borderBottom: '1px solid var(--color-border)'
                        }}>
                            <h3 style={{ margin: 0 }}>Select from Library</h3>
                            <button
                                onClick={() => {
                                    setShowLibraryPicker(false);
                                    setAddLibraryImagesCallback(null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--color-text-secondary)',
                                    padding: 'var(--space-1)'
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Search */}
                        <div style={{ padding: 'var(--space-3) var(--space-4) 0' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border)'
                            }}>
                                <Search size={16} style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }} />
                                <input
                                    type="text"
                                    placeholder="Search images..."
                                    value={librarySearch}
                                    onChange={(e) => setLibrarySearch(e.target.value)}
                                    style={{
                                        flex: 1,
                                        background: 'none',
                                        border: 'none',
                                        outline: 'none',
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-primary)'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Image Grid */}
                        <div style={{
                            flex: 1,
                            overflow: 'auto',
                            padding: 'var(--space-4)'
                        }}>
                            {libraryLoading ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 'var(--space-8)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    <Loader2 size={24} className="animate-spin" />
                                    <span style={{ marginLeft: 'var(--space-2)' }}>Loading library...</span>
                                </div>
                            ) : (() => {
                                const searchLower = librarySearch.toLowerCase().trim();
                                const filtered = searchLower
                                    ? libraryImages.filter(item =>
                                        (item.title || '').toLowerCase().includes(searchLower) ||
                                        (item.name || '').toLowerCase().includes(searchLower) ||
                                        (item.description || '').toLowerCase().includes(searchLower) ||
                                        (item.tags || []).some(t => t.toLowerCase().includes(searchLower)) ||
                                        (item.metadata?.pillarName || '').toLowerCase().includes(searchLower)
                                    )
                                    : libraryImages;

                                if (filtered.length === 0) {
                                    return (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: 'var(--space-8)',
                                            color: 'var(--color-text-tertiary)'
                                        }}>
                                            {librarySearch ? 'No images match your search' : 'No images in library'}
                                        </div>
                                    );
                                }

                                return (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                        gap: 'var(--space-3)'
                                    }}>
                                        {filtered.map(item => {
                                            const isSelected = librarySelected.some(s => s.id === item.id);
                                            return (
                                                <div
                                                    key={item.id}
                                                    onClick={() => toggleLibraryImage(item)}
                                                    style={{
                                                        position: 'relative',
                                                        borderRadius: 'var(--radius-md)',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        border: isSelected
                                                            ? '3px solid var(--color-primary)'
                                                            : '3px solid transparent',
                                                        transition: 'border-color 0.15s ease',
                                                        aspectRatio: '1'
                                                    }}
                                                >
                                                    <img
                                                        src={item.imageUrl}
                                                        alt={item.title || item.name || ''}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            display: 'block'
                                                        }}
                                                    />
                                                    {isSelected && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '6px',
                                                            right: '6px',
                                                            width: '24px',
                                                            height: '24px',
                                                            borderRadius: '50%',
                                                            background: 'var(--color-primary)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <CheckCircle size={16} style={{ color: 'white' }} />
                                                        </div>
                                                    )}
                                                    {/* Title overlay */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                                        padding: '16px 6px 4px',
                                                        fontSize: '10px',
                                                        color: 'white',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {item.title || item.name || 'Untitled'}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Footer */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 'var(--space-3) var(--space-4)',
                            borderTop: '1px solid var(--color-border)'
                        }}>
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                {librarySelected.length > 0
                                    ? `${librarySelected.length} image${librarySelected.length !== 1 ? 's' : ''} selected`
                                    : 'Click images to select'}
                            </span>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowLibraryPicker(false);
                                        setAddLibraryImagesCallback(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleConfirmLibrarySelection}
                                    disabled={librarySelected.length === 0}
                                >
                                    Add {librarySelected.length > 0 ? `(${librarySelected.length})` : ''}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Refinement Modal */}
            {showBulkRefinementModal && (
                <div
                    onClick={() => !isBulkRefining && setShowBulkRefinementModal(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 20000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: 'var(--color-bg-primary)',
                            borderRadius: 'var(--radius-lg)',
                            width: '600px',
                            maxWidth: '95vw',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{
                            padding: 'var(--space-4)',
                            borderBottom: '1px solid var(--color-border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <Wand2 size={20} style={{ color: 'var(--color-primary)' }} />
                                    Bulk Refine {selectedImages.length} Images
                                </h3>
                                <p style={{
                                    margin: 'var(--space-1) 0 0',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Apply the same refinement to all selected images
                                </p>
                            </div>
                            <button
                                onClick={() => !isBulkRefining && setShowBulkRefinementModal(false)}
                                disabled={isBulkRefining}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: isBulkRefining ? 'not-allowed' : 'pointer',
                                    color: 'var(--color-text-secondary)',
                                    padding: 'var(--space-1)',
                                    opacity: isBulkRefining ? 0.5 : 1
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Selected Images Preview */}
                        <div style={{
                            padding: 'var(--space-3) var(--space-4)',
                            borderBottom: '1px solid var(--color-border)',
                            background: 'var(--color-bg-secondary)'
                        }}>
                            <div style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                Selected images ({selectedImages.length})
                            </div>
                            <div style={{
                                display: 'flex',
                                gap: 'var(--space-2)',
                                overflowX: 'auto',
                                paddingBottom: 'var(--space-2)'
                            }}>
                                {selectedImages.map((img, idx) => {
                                    const refinementResult = bulkRefinementProgress.results?.find(
                                        r => r.briefId === img.briefId && r.cardIndex === img.cardIndex
                                    );
                                    const isProcessing = isBulkRefining &&
                                        bulkRefinementProgress.currentImage?.briefId === img.briefId &&
                                        bulkRefinementProgress.currentImage?.cardIndex === img.cardIndex;

                                    return (
                                        <div
                                            key={`${img.briefId}-${img.cardIndex ?? 'main'}-${idx}`}
                                            style={{
                                                position: 'relative',
                                                flexShrink: 0
                                            }}
                                        >
                                            <img
                                                src={img.imageUrl}
                                                alt={`Selected ${idx + 1}`}
                                                style={{
                                                    width: '64px',
                                                    height: '64px',
                                                    objectFit: 'cover',
                                                    borderRadius: 'var(--radius-sm)',
                                                    border: refinementResult?.success
                                                        ? '2px solid var(--success-500)'
                                                        : refinementResult?.success === false
                                                        ? '2px solid var(--error-500)'
                                                        : '1px solid var(--color-border)'
                                                }}
                                            />
                                            {/* Processing indicator */}
                                            {isProcessing && (
                                                <div style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    background: 'rgba(0,0,0,0.5)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: 'var(--radius-sm)'
                                                }}>
                                                    <Loader2 size={16} className="spin" style={{ color: 'white' }} />
                                                </div>
                                            )}
                                            {/* Success checkmark */}
                                            {refinementResult?.success && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '-4px',
                                                    right: '-4px',
                                                    background: 'var(--success-500)',
                                                    borderRadius: '50%',
                                                    width: '18px',
                                                    height: '18px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '2px solid white'
                                                }}>
                                                    <Check size={10} color="white" strokeWidth={3} />
                                                </div>
                                            )}
                                            {/* Error indicator */}
                                            {refinementResult?.success === false && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '-4px',
                                                    right: '-4px',
                                                    background: 'var(--error-500)',
                                                    borderRadius: '50%',
                                                    width: '18px',
                                                    height: '18px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '2px solid white'
                                                }}>
                                                    <X size={10} color="white" strokeWidth={3} />
                                                </div>
                                            )}
                                            {/* Card index label */}
                                            {img.cardIndex !== undefined && (
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '2px',
                                                    left: '2px',
                                                    background: 'rgba(0,0,0,0.6)',
                                                    color: 'white',
                                                    fontSize: '9px',
                                                    padding: '1px 4px',
                                                    borderRadius: '3px'
                                                }}>
                                                    Card {img.cardIndex + 1}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Refinement Panel or Progress */}
                        <div style={{ padding: 'var(--space-4)' }}>
                            {/* Show refinement panel when idle or before starting */}
                            {!isBulkRefining && bulkRefinementProgress.status !== 'complete' && (
                                <ImageRefinementPanel
                                    onRefine={handleBulkRefine}
                                    isRefining={isBulkRefining}
                                    error={bulkRefinementError}
                                    userId={currentUser?.uid}
                                    brandId={selectedBrand?.id}
                                    placeholder={`Describe the refinement to apply to all ${selectedImages.length} selected images...`}
                                    onBrowseLibrary={handleOpenLibraryPicker}
                                    showRegenerateOption={true}
                                />
                            )}

                            {/* Progress during refinement */}
                            {isBulkRefining && (
                                <div>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        <span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                            <Loader2 size={16} className="spin" style={{ color: 'var(--color-primary)' }} />
                                            Refining images...
                                        </span>
                                        <span style={{ color: 'var(--color-text-secondary)' }}>
                                            {bulkRefinementProgress.current} / {bulkRefinementProgress.total}
                                        </span>
                                    </div>
                                    <div style={{
                                        height: '8px',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-full)',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            height: '100%',
                                            width: `${(bulkRefinementProgress.current / bulkRefinementProgress.total) * 100}%`,
                                            background: 'var(--color-primary)',
                                            borderRadius: 'var(--radius-full)',
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                    <p style={{
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-secondary)',
                                        marginTop: 'var(--space-3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)'
                                    }}>
                                        <Clock size={14} />
                                        Processing with 15s delay between requests to respect rate limits...
                                    </p>
                                    {bulkRefinementProgress.current > 0 && (
                                        <p style={{
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--color-text-tertiary)',
                                            marginTop: 'var(--space-2)'
                                        }}>
                                            Estimated time remaining: ~{Math.ceil((bulkRefinementProgress.total - bulkRefinementProgress.current) * 25 / 60)} min
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Completion state */}
                            {bulkRefinementProgress.status === 'complete' && (
                                <div style={{ textAlign: 'center', padding: 'var(--space-4)' }}>
                                    <CheckCircle size={48} style={{ color: 'var(--success-500)', marginBottom: 'var(--space-3)' }} />
                                    <h4 style={{ margin: '0 0 var(--space-2) 0' }}>Bulk Refinement Complete</h4>
                                    <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                                        {bulkRefinementProgress.results.filter(r => r.success).length} of {bulkRefinementProgress.total} images refined successfully
                                    </p>
                                    {bulkRefinementProgress.results.some(r => !r.success) && (
                                        <p style={{
                                            color: 'var(--error-600)',
                                            fontSize: 'var(--font-size-sm)',
                                            marginTop: 'var(--space-2)'
                                        }}>
                                            {bulkRefinementProgress.results.filter(r => !r.success).length} images failed to refine
                                        </p>
                                    )}
                                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center', marginTop: 'var(--space-4)' }}>
                                        {bulkRefinementProgress.results.some(r => !r.success) && (
                                            <button
                                                onClick={() => {
                                                    // Filter to only failed images and retry
                                                    const failedImages = selectedImages.filter(img =>
                                                        bulkRefinementProgress.results.some(r =>
                                                            r.briefId === img.briefId &&
                                                            r.cardIndex === img.cardIndex &&
                                                            !r.success
                                                        )
                                                    );
                                                    setSelectedImages(failedImages);
                                                    setBulkRefinementProgress({
                                                        current: 0, total: 0, currentImage: null, status: 'idle', results: []
                                                    });
                                                }}
                                                className="btn btn-secondary"
                                            >
                                                <RefreshCw size={14} />
                                                Retry {bulkRefinementProgress.results.filter(r => !r.success).length} Failed
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowBulkRefinementModal(false);
                                                setBulkSelectionMode(false);
                                                setSelectedImages([]);
                                                setBulkRefinementProgress({
                                                    current: 0, total: 0, currentImage: null, status: 'idle', results: []
                                                });
                                                setExpandedCarouselBriefId(null);
                                            }}
                                            className="btn btn-primary"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BulkGenerationStep;
